import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { DesignerServicePanel } from '../components/DesignerServicePanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, PoundSterling, FileText, Image, Send, CheckCircle, XCircle, Plus, Trash2, Copy, Clock, Archive, Download, Boxes, Link as LinkIcon, Unlink, AlertTriangle, Wrench, Truck, Camera, Package, Mail, Ship, Eye, Paperclip, Upload, File as FileIcon, RotateCcw } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { STATUS_CONFIG } from '../components/StatusChart';
import AgreementSection from '../components/AgreementSection';
import MemorialApplicationSection from '../components/MemorialApplicationSection';
import FamilyPagePanel from '../components/FamilyPagePanel';

// Helper function to safely convert to number with default value
const safeNumber = (value, defaultValue = 0) => {
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

// Admin roles that have full access (including demo for tryout purposes)
const ADMIN_ROLES = ['admin', 'super_admin', 'company_admin', 'manager', 'demo'];

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, token, API } = useContext(AuthContext);
  
  // Check if user has admin-level access
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isStaffOrAdmin = user?.role === 'staff' || user?.role === 'workshop' || isAdmin;
  const canSeeMoney = isAdmin; // staff & workshop see standard info only, no financials
  
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [artwork, setArtwork] = useState([]);
  const [artworkLoadFailed, setArtworkLoadFailed] = useState(false);
  const [artworkVersions, setArtworkVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [artworkDialog, setArtworkDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [artworkPreviewDialog, setArtworkPreviewDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [fullSizeImageDialog, setFullSizeImageDialog] = useState(false);
  const [fullSizeImage, setFullSizeImage] = useState(null);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '', type: '', id: '' });

  // Demo mode email dialog state
  const [demoEmailDialog, setDemoEmailDialog] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoEmailType, setDemoEmailType] = useState(''); // 'artwork', 'quote', 'invoice'
  const [demoEmailItemId, setDemoEmailItemId] = useState('');
  const isDemo = user?.role === 'demo' || user?.is_demo;

  // Form states
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: '', notes: '' });
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: '', payment_method: '', notes: '' });
  const [quoteForm, setQuoteForm] = useState({ items: [{ description: '', amount: '' }], notes: '' });
  const [invoiceForm, setInvoiceForm] = useState({ amount: '', quote_id: '' });
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkLabel, setArtworkLabel] = useState('');
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [sendingAllArtwork, setSendingAllArtwork] = useState(false);
  const [eventDialog, setEventDialog] = useState(false);
  const [statusDate, setStatusDate] = useState(new Date().toISOString().slice(0, 16));
  const [users, setUsers] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    assigned_to: '',
    timer_duration_hours: '',
    due_date: ''
  });
  const [eventForm, setEventForm] = useState({ 
    event_type: 'note', 
    title: '', 
    description: '', 
    event_date: new Date().toISOString().slice(0, 16) 
  });

  // Stock matching states
  const [matchingStock, setMatchingStock] = useState([]);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [allocatedStock, setAllocatedStock] = useState(null);

  // Order number edit state
  const [editOrderNumberDialog, setEditOrderNumberDialog] = useState(false);
  const [newOrderNumber, setNewOrderNumber] = useState('');
  const [savingOrderNumber, setSavingOrderNumber] = useState(false);
  const [suggestedOrderNumber, setSuggestedOrderNumber] = useState('');

  // Memorial collection states
  const [collectionDialog, setCollectionDialog] = useState(false);
  const [collectionRecord, setCollectionRecord] = useState(null);
  const [collectionForm, setCollectionForm] = useState({
    memorial_details: '',
    name_on_stone: '',
    distinguishing_features: '',
    location: '',
    collected_by: ''
  });
  const [collectionPhoto, setCollectionPhoto] = useState(null);
  const [submittingCollection, setSubmittingCollection] = useState(false);
  const [collectionPreviewDialog, setCollectionPreviewDialog] = useState(false);

  // Manual artwork changes dialog state
  const [manualChangesDialog, setManualChangesDialog] = useState(false);
  const [manualChangeNotes, setManualChangeNotes] = useState('');
  const [submittingManualChanges, setSubmittingManualChanges] = useState(false);

  // Alternative email dialog state (for sending to different email than client)
  const [altEmailDialog, setAltEmailDialog] = useState(false);
  const [altEmail, setAltEmail] = useState('');
  const [altEmailType, setAltEmailType] = useState(''); // 'quote', 'invoice', 'artwork', 'all-artwork'
  const [altEmailItemId, setAltEmailItemId] = useState('');
  const [sendingWithAltEmail, setSendingWithAltEmail] = useState(false);
  const [collectionEmailPreview, setCollectionEmailPreview] = useState(null);
  const [sendingCollectionEmail, setSendingCollectionEmail] = useState(false);

  // Contact Client dialog state
  const [showContactClientDialog, setShowContactClientDialog] = useState(false);
  const [contactClientForm, setContactClientForm] = useState({
    to_email: '',
    subject: '',
    body: ''
  });
  // Attached files for the Contact Client email.
  // Shape: [{ filename, dataBase64, content_type, sizeBytes }]
  const [contactAttachments, setContactAttachments] = useState([]);
  const [sendingContactEmail, setSendingContactEmail] = useState(false);
  const [loadingContactTemplate, setLoadingContactTemplate] = useState(false);

  // Purchase Order states
  const [showPurchaseOrderDialog, setShowPurchaseOrderDialog] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [poTemplates, setPoTemplates] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [sendingPO, setSendingPO] = useState(false);
  const [poForm, setPoForm] = useState({
    supplier_id: '',
    supplier_email: '',
    supplier_name: '',
    stone_type: '',
    stone_code: '',
    stone_colour: '',
    shape: '',
    dimensions: { width: '', height: '', depth: '', thickness: '' },
    sundries: [],
    special_instructions: ''
  });

  // Fetch next available order number
  const fetchNextOrderNumber = async () => {
    try {
      const res = await axios.get(`${API}/orders/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestedOrderNumber(res.data.next_order_number);
      return res.data.next_order_number;
    } catch (error) {
      console.error('Failed to fetch next order number:', error);
      return null;
    }
  };

  // Handle order number save
  const handleSaveOrderNumber = async () => {
    if (!newOrderNumber.trim()) {
      toast.error('Order number cannot be empty');
      return;
    }
    try {
      setSavingOrderNumber(true);
      await axios.patch(`${API}/orders/${orderId}`, 
        { order_number: newOrderNumber.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditOrderNumberDialog(false);
      toast.success('Order number updated');
      // Refresh order data without showing another toast
      const res = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order number');
      toast.error(msg);
    } finally {
      setSavingOrderNumber(false);
    }
  };

  // Fetch suppliers and POs
  const fetchSuppliersAndPOs = async () => {
    try {
      const [suppRes, poRes, templatesRes] = await Promise.all([
        axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-orders?order_id=${orderId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/po-templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSuppliers(suppRes.data);
      setPurchaseOrders(poRes.data);
      setPoTemplates(templatesRes.data);
    } catch (error) {
      console.error('Failed to fetch suppliers/POs/templates:', error);
    }
  };

  // Load items from a PO template
  const loadFromTemplate = (template) => {
    const supplier = suppliers.find(s => s.id === template.supplier_id);
    setPoForm(prev => ({
      ...prev,
      supplier_id: template.supplier_id || '',
      supplier_email: supplier?.email || template.supplier_email || '',
      supplier_name: supplier?.name || template.supplier_name || '',
      sundries: template.items?.map(item => ({
        description: item.description,
        quantity: item.quantity || 1,
        include: true
      })) || []
    }));
  };

  // Open PO dialog with pre-filled data
  const openPurchaseOrderDialog = () => {
    setPoForm({
      supplier_id: '',
      supplier_email: '',
      supplier_name: '',
      stone_type: order?.stone_type || '',
      stone_code: order?.stone_code || '',
      stone_colour: order?.stone_colour || '',
      shape: '',
      dimensions: { width: '', height: '', depth: '', thickness: '' },
      sundries: order?.sundries?.map(s => ({ description: s.description || s.name, include: true })) || [],
      special_instructions: ''
    });
    fetchSuppliersAndPOs();
    setShowPurchaseOrderDialog(true);
  };

  // Handle supplier selection
  const handleSupplierSelect = (supplierId) => {
    if (supplierId === 'custom') {
      setPoForm(prev => ({ ...prev, supplier_id: '', supplier_name: '', supplier_email: '' }));
    } else {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier) {
        setPoForm(prev => ({
          ...prev,
          supplier_id: supplier.id,
          supplier_name: supplier.name,
          supplier_email: supplier.email
        }));
      }
    }
  };

  // Send purchase order
  const handleSendPurchaseOrder = async () => {
    if (!poForm.supplier_email) {
      toast.error('Supplier email is required');
      return;
    }
    try {
      setSendingPO(true);
      // Create PO
      const createRes = await axios.post(`${API}/purchase-orders`, {
        order_id: orderId,
        order_number: order?.order_number,
        ...poForm
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Send PO email
      await axios.post(`${API}/purchase-orders/${createRes.data.id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Purchase order sent to supplier');
      setShowPurchaseOrderDialog(false);
      fetchSuppliersAndPOs();
      fetchOrderData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send purchase order');
      toast.error(msg);
    } finally {
      setSendingPO(false);
    }
  };

  // Mark as stone ordered from PO
  const handleMarkStoneOrdered = async (poId) => {
    try {
      await axios.post(`${API}/purchase-orders/${poId}/mark-ordered`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order marked as Stone Ordered');
      fetchOrderData();
      fetchSuppliersAndPOs();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Order stone from China (Group Ordering) - navigates to group ordering with pre-filled data
  const handleOrderFromChina = () => {
    // Store order details in sessionStorage for the GroupOrdering page to pick up
    const chinaOrderData = {
      from_memorial_order: true,
      order_id: orderId,
      order_number: order.order_number,
      stone_colour: order.stone_colour || '',
      stone_type: order.stone_type || 'Granite',
      client_name: order.client_name || '',
      job_name: `Order #${order.order_number} - ${order.client_name || 'Memorial'}`,
    };
    
    sessionStorage.setItem('chinaOrderPrefill', JSON.stringify(chinaOrderData));
    
    // Navigate to group ordering page
    navigate('/group-ordering?from_order=true');
  };

  // Auto-populate quote from order
  const populateQuoteFromOrder = () => {
    if (!order) return;
    
    const items = [];
    
    // Add stone/memorial as first item
    items.push({
      description: `Memorial Stone - ${order.stone_type || 'Unknown'} (${order.stone_code || 'N/A'})`,
      amount: safeNumber(order.price).toString()
    });
    
    // Add sundries
    if (order.sundries && order.sundries.length > 0) {
      order.sundries.forEach(sundry => {
        items.push({
          description: sundry.description,
          amount: safeNumber(sundry.price).toString()
        });
      });
    }
    
    // Prefill quote notes from the order: general notes + (for maintenance jobs)
    // what the client requires — editable before sending
    const noteParts = [];
    const orderTypeNorm = (order.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (orderTypeNorm === 'maintenance' && order.maintenance_details) {
      noteParts.push(`Work required: ${order.maintenance_details}`);
    }
    if (order.notes) noteParts.push(order.notes);

    setQuoteForm({ 
      items: items.length > 0 ? items : [{ description: '', amount: '' }], 
      notes: noteParts.join('\n\n') 
    });
  };

  // Auto-populate invoice amount
  const populateInvoiceAmount = () => {
    if (!order) return;
    
    // Calculate total payments made
    const totalPaid = payments.reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
    
    // Calculate total including VAT and all items (same as balance calculation)
    let subtotal = safeNumber(order.price);
    
    // Add sundries to subtotal
    if (order.sundries && order.sundries.length > 0) {
      subtotal += order.sundries.reduce((sum, s) => sum + safeNumber(s.price), 0);
    }
    
    // Calculate VAT on subtotal
    const vat = subtotal * (safeNumber(order.vat_rate, 20) / 100);
    
    // Add cemetery fee (VAT exempt)
    const vatExemptTotal = safeNumber(order.cemetery_fee);
    
    // Total amount = subtotal + VAT + VAT exempt items
    const totalAmount = subtotal + vat + vatExemptTotal;
    
    // Calculate balance due
    const balanceDue = totalAmount - totalPaid;
    
    setInvoiceForm({ 
      amount: balanceDue > 0 ? balanceDue.toFixed(2) : '0.00', 
      quote_id: quotes.length > 0 ? quotes[quotes.length - 1].id : '' 
    });
  };

  useEffect(() => {
    fetchOrderData();
    
    // Acquire editing lock when opening the order
    const acquireLock = async () => {
      if (isAdmin && orderId) {
        try {
          const response = await axios.post(`${API}/orders/${orderId}/editing-lock`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.locked) {
            toast.warning(`This order is being edited by ${response.data.locked_by}`, {
              duration: 5000
            });
          }
        } catch (error) {
          console.error('Failed to acquire editing lock');
        }
      }
    };
    acquireLock();
    
    // Refresh lock every 2 minutes to keep it active
    const lockRefreshInterval = setInterval(acquireLock, 120000);
    
    // Release lock when leaving the page
    return () => {
      clearInterval(lockRefreshInterval);
      if (isAdmin && orderId) {
        axios.delete(`${API}/orders/${orderId}/editing-lock`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    };
  }, [orderId]);

  const fetchUsers = async (companyId = null) => {
    try {
      // Build URL with optional company_id parameter for proper data isolation
      let url = `${API}/users`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => ['admin', 'super_admin', 'company_admin', 'staff'].includes(u.role)));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchOrderData = async () => {
    try {
      // The order itself is critical — if this fails, we genuinely can't render the page.
      // All other fetches are best-effort: a transient blip on /payments or /artwork
      // must NOT bounce the user back to the dashboard.
      const orderRes = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(orderRes.data);

      const [paymentsRes, quotesRes, invoicesRes, artworkRes, timelineRes, attachmentsRes] = await Promise.all([
        axios.get(`${API}/payments/order/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/quotes/order/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices/order/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/artwork/order/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/orders/${orderId}/events`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/orders/${orderId}/attachments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      setPayments(paymentsRes.data || []);
      setQuotes(quotesRes.data || []);
      setInvoices(invoicesRes.data || []);
      // If the artwork fetch failed (data === null), keep the previous list instead of wiping it
      if (artworkRes.data !== null) {
        setArtworkLoadFailed(false);
        setArtwork(prev => {
          const prevById = Object.fromEntries(prev.map(p => [p.id, p]));
          return (artworkRes.data || []).map(a => a.file_data ? a : { ...a, file_data: prevById[a.id]?.file_data, rejection_file_data: prevById[a.id]?.rejection_file_data });
        });
        loadArtworkFiles(artworkRes.data || [], setArtwork);
      } else {
        setArtworkLoadFailed(true);
      }
      setTimeline(timelineRes.data || []);
      setAttachments(attachmentsRes.data || []);
      
      // Fetch users for this order's company (proper data isolation)
      fetchUsers(orderRes.data.company_id);
      
      // Check for allocated stock or matching stock
      if (orderRes.data.stock_item_id) {
        try {
          const stockRes = await axios.get(`${API}/memorial-stock/${orderRes.data.stock_item_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAllocatedStock(stockRes.data);
        } catch (e) {
          setAllocatedStock(null);
        }
      } else {
        // Fetch matching stock for this order
        try {
          const matchRes = await axios.get(`${API}/memorial-stock/match/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setMatchingStock(matchRes.data);
        } catch (e) {
          setMatchingStock([]);
        }
      }
      
      // Fetch memorial collection record for add-on inscription and restoration orders
      const fetchedOrderType = (orderRes.data.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (fetchedOrderType === 'add on inscription' || fetchedOrderType === 'restoration') {
        try {
          const collectionRes = await axios.get(`${API}/memorial-collection/order/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCollectionRecord(collectionRes.data);
        } catch (e) {
          setCollectionRecord(null);
        }
      }
      
      // Fetch all versions if current artwork exists
      if ((artworkRes.data || []).length > 0) {
        fetchArtworkVersions();
      }
    } catch (error) {
      // Only navigate away if the primary order fetch genuinely failed.
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('Order not found');
        navigate('/');
      } else if (status === 403) {
        toast.error('You do not have access to this order');
        navigate('/');
      } else if (status === 401) {
        // Auth interceptor / App-level useEffect will handle logout on next /auth/me check
        toast.error('Session expired — please sign in again');
      } else {
        // Transient error — keep the user on the page so they can retry without losing context
        toast.error('Could not load order data — please refresh');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchArtworkVersions = async () => {
    try {
      const response = await axios.get(`${API}/artwork/order/${orderId}/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtworkVersions(response.data);
      loadArtworkFiles(response.data || [], setArtworkVersions);
    } catch (error) {
      console.error('Failed to fetch artwork versions');
    }
  };

  // File payloads are excluded from list responses (they can be huge and cause timeouts).
  // Fetch each artwork's file individually and merge it into state as it arrives.
  const loadArtworkFiles = (list, setter) => {
    (list || []).forEach(async (a) => {
      if (a.file_data) return;
      try {
        const res = await axios.get(`${API}/artwork/${a.id}/file`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setter(prev => prev.map(p => p.id === a.id ? {
          ...p,
          file_data: res.data.file_data,
          rejection_file_data: res.data.rejection_file_data,
          content_type: res.data.content_type || p.content_type
        } : p));
      } catch (e) {
        // Non-fatal: preview just won't render for this item
      }
    });
  };

  // Stock management handlers
  const handleAllocateStock = async (stockId) => {
    try {
      await axios.post(`${API}/memorial-stock/${stockId}/allocate?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock allocated to this order');
      setShowStockDialog(false);
      fetchOrderData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to allocate stock');
      toast.error(msg);
    }
  };

  const handleDeallocateStock = async () => {
    if (!allocatedStock || !window.confirm('Remove stock allocation from this order?')) return;
    
    try {
      await axios.post(`${API}/memorial-stock/${allocatedStock.id}/deallocate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock deallocated from order');
      setAllocatedStock(null);
      fetchOrderData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to deallocate stock');
      toast.error(msg);
    }
  };

  // Memorial collection handlers
  const handleOpenCollectionDialog = () => {
    setCollectionForm({
      memorial_details: '',
      name_on_stone: order?.existing_name || '',
      distinguishing_features: '',
      location: order?.cemetery || '',
      collected_by: user?.name || ''
    });
    setCollectionPhoto(null);
    setCollectionDialog(true);
  };

  const handleSubmitCollection = async (e) => {
    e.preventDefault();
    if (submittingCollection) return;
    
    setSubmittingCollection(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('memorial_details', collectionForm.memorial_details);
      formData.append('name_on_stone', collectionForm.name_on_stone);
      formData.append('distinguishing_features', collectionForm.distinguishing_features || '');
      formData.append('location', collectionForm.location);
      formData.append('collected_by', collectionForm.collected_by);
      if (collectionPhoto) {
        formData.append('photo', collectionPhoto);
      }

      const response = await axios.post(`${API}/memorial-collection`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout for large photos
      });

      setCollectionRecord(response.data);
      setCollectionDialog(false);
      toast.success('Memorial collection recorded');
      
      // Fetch email preview
      try {
        const previewRes = await axios.get(`${API}/memorial-collection/${response.data.id}/preview-email`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCollectionEmailPreview(previewRes.data);
        setCollectionPreviewDialog(true);
      } catch (e) {
        console.error('Failed to fetch preview', e);
      }
      
      fetchOrderData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to record collection');
      toast.error(msg);
    } finally {
      setSubmittingCollection(false);
    }
  };

  const handlePreviewCollectionEmail = async () => {
    if (!collectionRecord) return;
    try {
      const previewRes = await axios.get(`${API}/memorial-collection/${collectionRecord.id}/preview-email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollectionEmailPreview(previewRes.data);
      setCollectionPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  const handleSendCollectionEmail = async () => {
    if (!collectionEmailPreview?.collection_id) return;
    setSendingCollectionEmail(true);
    try {
      await axios.post(`${API}/memorial-collection/${collectionEmailPreview.collection_id}/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email sent to client');
      setCollectionPreviewDialog(false);
      setCollectionEmailPreview(null);
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingCollectionEmail(false);
    }
  };

  // Contact Client Functions
  const handleOpenContactClient = async () => {
    setLoadingContactTemplate(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/contact-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactClientForm({
        to_email: response.data.to_email || '',
        subject: response.data.subject || '',
        body: response.data.body || ''
      });
      setShowContactClientDialog(true);
    } catch (error) {
      console.error('Template load error:', error.response?.data || error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to load contact template');
      toast.error(msg);
      // Fallback to empty form
      setContactClientForm({
        to_email: order?.client_email || '',
        subject: `Regarding Your Order #${order?.order_number}`,
        body: `Dear ${order?.client_name || 'Customer'},\n\nI hope this message finds you well.\n\nI am writing regarding your order #${order?.order_number}.\n\n[Your message here]\n\nPlease feel free to reply to this email or call us if you have any questions.\n\nKind regards`
      });
      setShowContactClientDialog(true);
    } finally {
      setLoadingContactTemplate(false);
    }
  };

  const handleSendContactEmail = async () => {
    if (!contactClientForm.to_email || !contactClientForm.subject || !contactClientForm.body) {
      toast.error('Please fill in all fields');
      return;
    }

    setSendingContactEmail(true);
    try {
      await axios.post(`${API}/orders/${orderId}/contact-client`, {
        order_id: orderId,
        to_email: contactClientForm.to_email,
        subject: contactClientForm.subject,
        body: contactClientForm.body,
        // Strip the local-only sizeBytes before sending — backend only needs
        // filename + base64 data + content_type.
        attachments: contactAttachments.map(a => ({
          filename: a.filename,
          data: a.dataBase64,
          content_type: a.content_type
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        contactAttachments.length
          ? `Email sent to client with ${contactAttachments.length} attachment${contactAttachments.length === 1 ? '' : 's'}`
          : 'Email sent to client successfully'
      );
      setShowContactClientDialog(false);
      setContactClientForm({ to_email: '', subject: '', body: '' });
      setContactAttachments([]);
    } catch (error) {
      console.error('Failed to send email to client:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      const errorDetail = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : (error.message || 'Failed to send email'));
      toast.error(errorDetail);
    } finally {
      setSendingContactEmail(false);
    }
  };

  /**
   * Add one or more files to the Contact Client email. Each file is read as a
   * data URL, the base64 payload is extracted, and we cap the *cumulative*
   * payload at 10 MB so the SMTP relay doesn't reject the message.
   */
  const handleAddContactAttachment = async (files) => {
    if (!files || !files.length) return;
    const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
    const currentBytes = contactAttachments.reduce((s, a) => s + (a.sizeBytes || 0), 0);
    const newAttachments = [];
    for (const file of files) {
      if (currentBytes + newAttachments.reduce((s, a) => s + a.sizeBytes, 0) + file.size > MAX_TOTAL_BYTES) {
        toast.error(`"${file.name}" would exceed the 10MB attachment limit — skipped.`);
        continue;
      }
      // FileReader.readAsDataURL → "data:image/png;base64,<payload>"
      // We only want the payload portion for the API.
      try {
        const dataBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const idx = result.indexOf(',');
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = () => reject(reader.error || new Error('Read failed'));
          reader.readAsDataURL(file);
        });
        newAttachments.push({
          filename: file.name,
          dataBase64,
          content_type: file.type || 'application/octet-stream',
          sizeBytes: file.size
        });
      } catch (e) {
        toast.error(`Failed to read "${file.name}"`);
      }
    }
    if (newAttachments.length) {
      setContactAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const handleRemoveContactAttachment = (index) => {
    setContactAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleResendCollectionEmail = async () => {
    if (!collectionRecord) return;
    // Show preview first instead of sending directly
    handlePreviewCollectionEmail();
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        order_id: orderId,
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Payment recorded successfully');
      setPaymentDialog(false);
      setPaymentForm({ amount: '', payment_method: '', notes: '' });
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentForm({
      amount: payment.amount.toString(),
      payment_method: payment.payment_method,
      notes: payment.notes || ''
    });
    setEditPaymentDialog(true);
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/payments/${selectedPayment.id}`, {
        amount: parseFloat(editPaymentForm.amount),
        payment_method: editPaymentForm.payment_method,
        notes: editPaymentForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Payment updated successfully');
      setEditPaymentDialog(false);
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      await axios.delete(`${API}/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment deleted successfully');
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleMarkInvoicePaid = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/mark-paid`, {
        amount: selectedInvoice.amount,
        payment_method: 'Invoice Payment',
        notes: `Payment for invoice ${selectedInvoice.id}`
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Invoice marked as paid and payment recorded');
      setMarkPaidDialog(false);
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      const items = quoteForm.items.map(item => ({
        description: item.description,
        amount: parseFloat(item.amount)
      }));
      
      // Add lettering cost to items if it exists
      if (safeNumber(order.lettering_cost) > 0) {
        items.push({
          description: `Extra Lettering (${(order.lettering_type || 'standard').replace(/_/g, ' ')})`,
          amount: safeNumber(order.lettering_cost)
        });
      }
      
      const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
      
      // Add cemetery fee as VAT-exempt item if it exists
      const vat_exempt_items = [];
      if (safeNumber(order.cemetery_fee) > 0) {
        vat_exempt_items.push({
          description: 'Cemetery/Church Application Fee',
          amount: safeNumber(order.cemetery_fee)
        });
      }
      const vat_exempt_total = vat_exempt_items.reduce((sum, item) => sum + item.amount, 0);
      
      // Use order's VAT rate, defaulting to 20 only if not set (check for null/undefined, not falsy)
      const vat_rate = safeNumber(order.vat_rate, 20);
      const tax = subtotal * (vat_rate / 100);
      const total = subtotal + tax + vat_exempt_total;

      await axios.post(`${API}/quotes`, {
        order_id: orderId,
        items,
        vat_exempt_items,
        subtotal,
        vat_exempt_total,
        vat_rate,
        tax,
        total,
        notes: quoteForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Quote created successfully');
      setQuoteDialog(false);
      setQuoteForm({ items: [{ description: '', amount: '' }], notes: '' });
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to create quote');
    }
  };

  // Open alternative email dialog before sending
  const openAltEmailDialog = (type, itemId = '') => {
    setAltEmailType(type);
    setAltEmailItemId(itemId);
    setAltEmail('');
    setAltEmailDialog(true);
  };

  // Handle sending with optional alternative email
  const handleSendWithAltEmail = async () => {
    setSendingWithAltEmail(true);
    try {
      const emailToUse = altEmail.trim() || null;
      
      if (altEmailType === 'quote') {
        await axios.post(`${API}/quotes/${altEmailItemId}/send`, 
          { override_email: emailToUse },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(emailToUse ? `Quote sent to ${emailToUse}` : 'Quote sent to client');
      } else if (altEmailType === 'invoice') {
        await axios.post(`${API}/invoices/${altEmailItemId}/send`, 
          { override_email: emailToUse },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(emailToUse ? `Invoice sent to ${emailToUse}` : 'Invoice sent to client');
      } else if (altEmailType === 'artwork') {
        const response = await axios.post(`${API}/artwork/${altEmailItemId}/send`, 
          { override_email: emailToUse },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.success === false) {
          // Backend returns 200 with the real SMTP error — surface it so failures are diagnosable
          toast.error(response.data.message || response.data.error || 'Failed to send artwork email');
          return;
        }
        toast.success(emailToUse ? `Artwork sent to ${emailToUse}` : 'Artwork sent to client');
        // Optimistically mark as sent so the button updates even if the refresh is slow
        setArtwork(prev => prev.map(a => a.id === altEmailItemId ? { ...a, sent_at: new Date().toISOString() } : a));
        setArtworkPreviewDialog(false);
      } else if (altEmailType === 'all-artwork') {
        const response = await axios.post(`${API}/orders/${orderId}/send-all-artwork`, 
          { override_email: emailToUse },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(emailToUse ? `Artwork sent to ${emailToUse}` : (response.data.message || 'Artwork sent to client'));
        setArtwork(prev => prev.map(a => a.status === 'pending' ? { ...a, sent_at: new Date().toISOString() } : a));
      }
      
      setAltEmailDialog(false);
      fetchOrderData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : (error.message ? `Failed to send: ${error.message}` : 'Failed to send'));
      toast.error(msg);
    } finally {
      setSendingWithAltEmail(false);
    }
  };

  const handleSendQuote = async (quoteId) => {
    // If demo user, open email dialog instead of sending directly
    if (isDemo) {
      openDemoEmailDialog('quote', quoteId);
      return;
    }

    // Open alternative email dialog to allow sending to different email
    openAltEmailDialog('quote', quoteId);
  };

  const handlePreviewQuote = async (quoteId) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewContent({ 
        subject: response.data.subject, 
        body: response.data.body,
        type: 'quote',
        id: quoteId
      });
      setPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const handlePreviewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewContent({ 
        subject: response.data.subject, 
        body: response.data.body,
        type: 'invoice',
        id: invoiceId
      });
      setPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId) => {
    try {
      // Open PDF view in new window
      const pdfUrl = `${API}/invoices/${invoiceId}/pdf`;
      const authHeader = `Bearer ${token}`;
      
      // Open in new window with auth
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write('<html><body><p>Loading invoice...</p></body></html>');
        
        const response = await axios.get(pdfUrl, {
          headers: { Authorization: authHeader }
        });
        
        newWindow.document.write(response.data);
        newWindow.document.close();
      } else {
        toast.error('Please allow popups to view invoice');
      }
    } catch (error) {
      toast.error('Failed to load invoice PDF');
    }
  };

  const handleConfirmSend = async () => {
    if (previewContent.type === 'quote') {
      await handleSendQuote(previewContent.id);
    } else if (previewContent.type === 'invoice') {
      await handleSendInvoice(previewContent.id);
    }
    setPreviewDialog(false);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        order_id: orderId,
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        assigned_to: taskForm.assigned_to || null,
        timer_duration_hours: taskForm.timer_duration_hours ? parseInt(taskForm.timer_duration_hours) : null,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null
      };

      await axios.post(`${API}/tasks`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Task created successfully');
      setTaskDialog(false);
      setTaskForm({
        title: '',
        description: '',
        priority: 'normal',
        assigned_to: '',
        timer_duration_hours: '',
        due_date: ''
      });
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote deleted successfully');
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, {
        order_id: orderId,
        amount: parseFloat(invoiceForm.amount),
        quote_id: invoiceForm.quote_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Invoice created successfully');
      setInvoiceDialog(false);
      setInvoiceForm({ amount: '', quote_id: '' });
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    // If demo user, open email dialog instead of sending directly
    if (isDemo) {
      openDemoEmailDialog('invoice', invoiceId);
      return;
    }

    // Open alternative email dialog to allow sending to different email
    openAltEmailDialog('invoice', invoiceId);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted successfully');
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this timeline event? This will affect traffic light indicators.')) {
      return;
    }
    try {
      await axios.delete(`${API}/orders/${orderId}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleDeleteAllEvents = async () => {
    if (!window.confirm('Are you sure you want to delete ALL timeline events? This will reset all traffic light indicators to red. This action cannot be undone!')) {
      return;
    }
    try {
      await axios.delete(`${API}/orders/${orderId}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All events deleted successfully');
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to delete events');
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/orders/${orderId}/events`, {
        order_id: orderId,
        event_type: eventForm.event_type,
        title: eventForm.title,
        description: eventForm.description,
        event_date: new Date(eventForm.event_date).toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Timeline event added');
      setEventDialog(false);
      setEventForm({ 
        event_type: 'note', 
        title: '', 
        description: '', 
        event_date: new Date().toISOString().slice(0, 16) 
      });
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleUploadArtwork = async (e) => {
    e.preventDefault();
    if (!artworkFile) {
      toast.error('Please select a file');
      return;
    }

    // Prevent multiple submissions
    if (uploadingArtwork) {
      return;
    }

    // File size validation (50MB frontend limit - backend will auto-compress if needed)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (artworkFile.size > maxSize) {
      toast.error(`File is too large (${(artworkFile.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 50MB.`);
      return;
    }

    try {
      setUploadingArtwork(true);
      const formData = new FormData();
      formData.append('file', artworkFile);
      formData.append('order_id', orderId);
      if (artworkLabel.trim()) {
        formData.append('label', artworkLabel.trim());
      }

      const response = await axios.post(`${API}/artwork`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout
      });

      const uploadedSize = artworkFile.size / (1024 * 1024);
      const labelText = response.data?.label ? ` (${response.data.label})` : '';
      if (uploadedSize > 12) {
        toast.success(`Artwork${labelText} uploaded successfully (auto-compressed)`);
      } else {
        toast.success(`Artwork${labelText} uploaded successfully`);
      }
      setArtworkDialog(false);
      setArtworkFile(null);
      setArtworkLabel('');
      fetchOrderData();
    } catch (error) {
      console.error('Artwork upload error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Failed to upload artwork';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error - please check your connection';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out - file may be too large';
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingArtwork(false);
    }
  };

  // Send all pending artwork to client
  const handleSendAllArtwork = async () => {
    const pendingArtwork = artwork.filter(a => a.status === 'pending' && !a.sent_at);
    if (pendingArtwork.length === 0) {
      toast.error('No pending artwork to send');
      return;
    }
    
    // If demo user, open email dialog instead of sending directly
    if (isDemo) {
      openDemoEmailDialog('artwork', orderId);
      return;
    }
    
    // Open alternative email dialog to allow sending to different email
    openAltEmailDialog('all-artwork', orderId);
  };

  const handleArtworkAction = async (artworkId, status) => {
    try {
      const response = await axios.patch(`${API}/artwork/${artworkId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Artwork ${status}`);
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to update artwork status');
    }
  };

  const handlePreviewArtwork = async (artwork) => {
    try {
      // Fetch the actual email preview from backend
      const response = await axios.get(`${API}/artwork/${artwork.id}/preview-email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedArtwork({
        ...artwork,
        emailPreview: response.data
      });
      setArtworkPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  // Open demo email dialog for artwork/quote/invoice
  const openDemoEmailDialog = (type, itemId) => {
    setDemoEmailType(type);
    setDemoEmailItemId(itemId);
    setDemoEmail('');
    setDemoEmailDialog(true);
  };

  // Handle sending demo email
  const handleSendDemoEmail = async () => {
    if (!demoEmail || !demoEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      let endpoint = '';
      if (demoEmailType === 'artwork') {
        endpoint = `${API}/artwork/${demoEmailItemId}/send-demo`;
      } else if (demoEmailType === 'quote') {
        endpoint = `${API}/quotes/${demoEmailItemId}/send-demo`;
      } else if (demoEmailType === 'invoice') {
        endpoint = `${API}/invoices/${demoEmailItemId}/send-demo`;
      }

      await axios.post(endpoint, { demo_email: demoEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${demoEmailType.charAt(0).toUpperCase() + demoEmailType.slice(1)} sent to ${demoEmail}! Check your inbox.`);
      setDemoEmailDialog(false);
      setArtworkPreviewDialog(false);
      fetchOrderData();
    } catch (error) {
      toast.error(`Failed to send ${demoEmailType}`);
    }
  };

  const handleSendArtwork = async (artworkId) => {
    // If demo user, open email dialog instead of sending directly
    if (isDemo) {
      openDemoEmailDialog('artwork', artworkId);
      return;
    }

    // Open alternative email dialog to allow sending to different email
    openAltEmailDialog('artwork', artworkId);
  };

  const handleRestoreArtwork = async (artworkId, version) => {
    if (!window.confirm(`Restore version ${version}? This will archive the current version.`)) {
      return;
    }
    try {
      await axios.post(`${API}/artwork/${artworkId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Version ${version} restored successfully`);
      fetchOrderData();
    } catch (error) {
      toast.error('Failed to restore artwork');
    }
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm('Are you sure you want to delete this order? This will permanently delete the order and all related data (payments, quotes, invoices, artwork, tasks). This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Delete order error:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : (error.message || 'Failed to delete order'));
      toast.error(errorMsg);
    }
  };

  const handleDuplicateOrder = async () => {
    try {
      const response = await axios.post(`${API}/orders/${orderId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order duplicated successfully');
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to duplicate order');
    }
  };

  const handleArchiveOrder = async () => {
    if (!window.confirm('Archive this order? This will move it to the archived orders list. You can access archived orders from the dashboard.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/orders/${orderId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order archived successfully');
      navigate('/'); // Return to dashboard
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to archive order');
      toast.error(errorMsg);
    }
  };

  const addQuoteItem = () => {
    setQuoteForm({
      ...quoteForm,
      items: [...quoteForm.items, { description: '', amount: '' }]
    });
  };

  const removeQuoteItem = (index) => {
    const newItems = quoteForm.items.filter((_, i) => i !== index);
    setQuoteForm({ ...quoteForm, items: newItems });
  };

  const updateQuoteItem = (index, field, value) => {
    const newItems = [...quoteForm.items];
    newItems[index][field] = value;
    setQuoteForm({ ...quoteForm, items: newItems });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-slate-600">Loading order...</div>
      </div>
    );
  }

  // The order fetch can fail transiently (slow load / timeout / 500) or return
  // nothing. Previously the render fell through and crashed on `order.price`
  // (TypeError: can't access property "price", order is null), white-screening
  // the app and logging the user out. Show a recoverable state instead.
  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center" data-testid="order-load-failed">
          <div className="text-slate-800 text-lg font-semibold mb-2">This order couldn't be loaded</div>
          <p className="text-sm text-slate-500 mb-6">
            The order data didn't finish loading — this is usually a temporary connection hiccup. You're still signed in. Please try again.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => { setLoading(true); fetchOrderData(); }}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              data-testid="retry-load-order-btn">
              Retry
            </button>
            <button
              onClick={() => navigate('/')}
              className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-md text-sm font-medium"
              data-testid="back-to-dashboard-btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalPaid = payments.reduce((sum, p) => sum + safeNumber(p.amount), 0);
  
  // Calculate total including VAT and all items (matching invoice calculation)
  let subtotal = safeNumber(order.price);
  
  // Add sundries to subtotal
  if (order.sundries && order.sundries.length > 0) {
    subtotal += order.sundries.reduce((sum, s) => sum + safeNumber(s.price), 0);
  }
  
  // Calculate VAT on subtotal
  const vatRate = safeNumber(order.vat_rate, 20);
  const vat = subtotal * (vatRate / 100);
  
  // Add cemetery fee (VAT exempt)
  const vatExemptTotal = safeNumber(order.cemetery_fee);
  
  // Total amount = subtotal + VAT + VAT exempt items
  const totalAmount = subtotal + vat + vatExemptTotal;

  // Collection applies to add-on inscription and restoration orders.
  // Normalise the order type so variants like 'Add On Inscription' / 'add-on-inscription' still match.
  const normalizedOrderType = (order?.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  const isCollectionOrderType = normalizedOrderType === 'add on inscription' || normalizedOrderType === 'restoration';
  
  // Balance = total amount - total paid
  const balance = totalAmount - totalPaid;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          {isStaffOrAdmin && (
            <div className="flex gap-2 w-full sm:w-auto flex-wrap">
              {isAdmin && (
                <Button
                  variant="default"
                  onClick={() => navigate(`/edit-order/${orderId}`)}
                  className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-700"
                  data-testid="edit-order-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Edit Order
                </Button>
              )}
              {/* Save Order Summary PDF — full snapshot: all data, status, timeline */}
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    toast.info('Preparing PDF…');
                    const res = await axios.get(`${API}/orders/${orderId}/summary-pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Order_Summary_${order?.order_number || orderId}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Order PDF downloaded');
                  } catch (err) {
                    toast.error('Could not generate the order PDF.');
                  }
                }}
                className="flex-1 sm:flex-none border-slate-300"
                data-testid="download-order-summary-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Save Order PDF
              </Button>
              {/* Contact Client Button */}
              {order?.client_email && (
                <Button
                  variant="default"
                  onClick={handleOpenContactClient}
                  disabled={loadingContactTemplate}
                  className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-700"
                  data-testid="contact-client-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {loadingContactTemplate ? 'Loading...' : 'Contact Client'}
                </Button>
              )}
              {/* Memorial Collection Button - for add-on inscription and restoration orders */}
              {isCollectionOrderType && !collectionRecord && (
                <Button
                  variant="default"
                  onClick={handleOpenCollectionDialog}
                  className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700"
                  data-testid="collect-memorial-btn"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Record Collection
                </Button>
              )}
              {/* Collected indicator — visible at a glance in the header */}
              {isCollectionOrderType && collectionRecord && (
                <div
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 text-sm font-medium"
                  data-testid="memorial-collected-badge"
                  title={`Collected by ${collectionRecord.collected_by || 'unknown'}`}
                >
                  <Truck className="w-4 h-4" />
                  Collected{collectionRecord.collected_at ? ` ${new Date(collectionRecord.collected_at).toLocaleDateString('en-GB')}` : ''}
                </div>
              )}
              {/* Order Stone — quick-create a Group Order pre-filled from this
                  memorial. Only enabled once the application is approved (or the
                  order has progressed beyond that). Super admin can use anytime. */}
              {order?.order_type !== 'grave vault' && order?.order_type !== 'maintenance' && (
                <Button
                  variant="default"
                  onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/group-orders/prefill-from-order/${orderId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const p = r.data || {};
                      sessionStorage.setItem('chinaOrderPrefill', JSON.stringify({
                        order_id: p.order_id,
                        order_number: p.order_number,
                        client_name: p.client_name,
                        job_name: `${p.order_number} — ${p.client_name}`,
                        stone_colour: p.stone_colour,
                        stone_type: p.stone_type,
                        memorial_type: p.memorial_type,
                        components: p.components,
                      }));
                      navigate('/group-ordering');
                    } catch (e) {
                      const msg = e.response?.data?.detail || 'Failed to start stone order';
                      toast.error(typeof msg === 'string' ? msg : 'This memorial isn\'t ready for stone order yet.');
                    }
                  }}
                  className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700"
                  data-testid="order-stone-btn"
                  title={
                    ['application-approved','stone-ordered','stone-in-stock','work-started','memorial-completed'].includes(order?.status)
                      ? 'Pre-fill Group Ordering from this order'
                      : 'Available once the order status is "Application Approved" or later'
                  }
                >
                  <Package className="w-4 h-4 mr-2" />
                  Order Stone
                </Button>
              )}
              <Button
                variant="default"
                onClick={() => setTaskDialog(true)}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                data-testid="add-task-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/orders/${orderId}/job-sheet`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'text'
                      });
                      
                      // Create a blob from the HTML
                      const blob = new Blob([response.data], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      
                      // Open in new window
                      const newWindow = window.open(url, '_blank');
                      
                      if (!newWindow) {
                        toast.error('Please allow pop-ups for this site to view job sheet');
                      }
                      
                      // Clean up the blob URL after a short delay
                      setTimeout(() => URL.revokeObjectURL(url), 100);
                    } catch (error) {
                      console.error('Job sheet error:', error);
                      const detail = error.response?.data?.detail;
                      const errorMsg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : (error.message || 'Failed to generate job sheet'));
                      toast.error(errorMsg);
                    }
                  }}
                  className="flex-1 sm:flex-none bg-blue-50 hover:bg-blue-100"
                  data-testid="job-sheet-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Job Sheet
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleDuplicateOrder}
                className="flex-1 sm:flex-none"
                data-testid="duplicate-order-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Duplicate Order
              </Button>
              {isAdmin && order?.status === 'memorial-completed' && !order?.is_archived && (
                <Button
                  variant="outline"
                  onClick={handleArchiveOrder}
                  className="flex-1 sm:flex-none bg-amber-50 hover:bg-amber-100 border-amber-300"
                  data-testid="archive-order-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Order
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={handleDeleteOrder}
                  className="flex-1 sm:flex-none text-red-600 hover:bg-red-50 border-red-300"
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Order
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Family Page — private no-login portal for the family */}
        {isStaffOrAdmin && <div className="mb-6"><FamilyPagePanel orderId={orderId} token={token} API={API} /></div>}

        {/* Order Summary */}
        <Card className="mb-6 border-0 shadow-xl" data-testid="order-summary">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <CardTitle className="text-2xl sm:text-3xl">{order.client_name}</CardTitle>
                  {order.order_number && isStaffOrAdmin ? (
                    <Badge 
                      className="bg-indigo-100 text-indigo-800 text-lg px-3 py-1 font-mono cursor-pointer hover:bg-indigo-200 transition-colors"
                      onClick={() => {
                        setNewOrderNumber(order.order_number);
                        setEditOrderNumberDialog(true);
                      }}
                      title="Click to edit order number"
                    >
                      #{order.order_number}
                    </Badge>
                  ) : order.order_number && (
                    <Badge className="bg-indigo-100 text-indigo-800 text-lg px-3 py-1 font-mono">
                      #{order.order_number}
                    </Badge>
                  )}
                </div>
                <p className="text-blue-600 font-medium text-base sm:text-lg capitalize mb-2">{order.order_type}</p>
                <p className="text-slate-500 text-sm sm:text-base">{order.client_email}</p>
                {order.client_phone && <p className="text-slate-500 text-sm sm:text-base">{order.client_phone}</p>}
                {order.customer_address && (
                  <p className="text-slate-500 text-sm sm:text-base whitespace-pre-wrap">{order.customer_address}</p>
                )}
              </div>
              <div className="flex flex-col gap-3 w-full sm:w-auto">
                {isStaffOrAdmin && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={statusDate}
                        onChange={(e) => setStatusDate(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Status</Label>
                      <select
                        value={order.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          
                          // Special handling for changes-requested status - show dialog to enter notes
                          if (newStatus === 'changes-requested') {
                            setManualChangeNotes('');
                            setManualChangesDialog(true);
                            return;
                          }
                          
                          // Special handling for memorial-completed status
                          if (newStatus === 'memorial-completed') {
                            const archiveChoice = window.confirm('Status will be updated to Memorial Completed. Would you like to archive this order?\n\nClick OK to archive, or Cancel to mark as completed without archiving.');
                            
                            try {
                              await axios.patch(`${API}/orders/${orderId}`, 
                                { 
                                  status: newStatus,
                                  status_date: new Date(statusDate).toISOString()
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              // Archive if user chose to
                              if (archiveChoice) {
                                await axios.post(`${API}/orders/${orderId}/archive`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Order completed and archived');
                              } else {
                                toast.success('Status updated to completed');
                              }
                              
                              setStatusDate(new Date().toISOString().slice(0, 16));
                              fetchOrderData();
                            } catch (error) {
                              toast.error('Failed to update status');
                            }
                          } else {
                            // Normal status update
                            if (!window.confirm('Update order status? This will be logged to the timeline.')) {
                              return;
                            }
                          try {
                            await axios.patch(`${API}/orders/${orderId}`, 
                              { 
                                status: newStatus,
                                status_date: new Date(statusDate).toISOString()
                              },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success('Status updated');
                            setStatusDate(new Date().toISOString().slice(0, 16));
                            fetchOrderData();
                          } catch (error) {
                            toast.error('Failed to update status');
                          }
                        }
                      }}
                      className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      data-testid="status-select"
                    >
                      <option value="quote">Quote</option>
                      <option value="accepted">Accepted</option>
                      <option value="deposit-taken">Deposit Taken</option>
                      <option value="artwork-created">Artwork Created</option>
                      <option value="artwork-approved">Artwork Approved</option>
                      <option value="changes-requested">Changes Requested</option>
                      <option value="application-sent">Application Sent</option>
                      <option value="application-fee-paid">Application Fee Paid</option>
                      <option value="application-approved">Application Approved</option>
                      <option value="stone-ordered">Stone Ordered</option>
                      <option value="stone-in-stock">Stone in Stock</option>
                      <option value="work-started">Work Started</option>
                      <option value="memorial-completed">Memorial Completed</option>
                    </select>
                    </div>
                    
                    {/* Purchase Order Button - Show when application approved */}
                    {(isAdmin || user?.role === 'staff' || user?.role === 'workshop') && order.status === 'application-approved' && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800 mb-2 font-medium">Ready to order stone?</p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={openPurchaseOrderDialog}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid="create-po-btn"
                          >
                            <Truck className="w-4 h-4 mr-2" />
                            Create Purchase Order
                          </Button>
                          <Button
                            onClick={() => handleOrderFromChina()}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="order-stone-btn"
                          >
                            <Ship className="w-4 h-4 mr-2" />
                            Order Stone
                          </Button>
                        </div>
                        <p className="text-xs text-orange-600 mt-2">
                          Use "Order Stone" to create a trade wholesale stone order and automatically mark this order as Stone Ordered.
                        </p>
                      </div>
                    )}
                    
                    {/* Delivery ETA - Show when stone ordered or later - ONLY for new memorial orders */}
                    {(isAdmin || user?.role === 'staff' || user?.role === 'workshop') && 
                     order.order_type === 'new memorial' &&
                     ['stone-ordered', 'stone-in-stock', 'work-started'].includes(order.status) && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Label className="text-sm font-semibold text-amber-900 mb-2 block">
                          📦 Delivery/Materials ETA
                        </Label>
                        <Input
                          type="date"
                          value={order.delivery_eta || ''}
                          onChange={async (e) => {
                            try {
                              await axios.patch(`${API}/orders/${orderId}`, 
                                { delivery_eta: e.target.value },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success('Delivery ETA updated');
                              fetchOrderData();
                            } catch (error) {
                              toast.error('Failed to update ETA');
                            }
                          }}
                          className="bg-white"
                        />
                        {order.delivery_eta && (
                          <p className="text-xs text-amber-700 mt-1">
                            Expected: {new Date(order.delivery_eta).toLocaleDateString('en-GB', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Earliest Fitting Date - for add-on inscriptions when work is done */}
                    {(isAdmin || user?.role === 'staff' || user?.role === 'workshop') && 
                     order.order_type === 'add on inscription' &&
                     ['work-started', 'memorial-completed'].includes(order.status) && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <Label className="text-sm font-semibold text-purple-900 mb-2 block">
                          📅 Earliest Fitting Date
                        </Label>
                        <p className="text-xs text-purple-700 mb-2">
                          Authority restriction - memorial cannot be fitted before this date
                        </p>
                        <Input
                          type="date"
                          value={order.earliest_fitting_date || ''}
                          onChange={async (e) => {
                            try {
                              await axios.patch(`${API}/orders/${orderId}`, 
                                { earliest_fitting_date: e.target.value },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success('Earliest fitting date updated');
                              fetchOrderData();
                            } catch (error) {
                              toast.error('Failed to update fitting date');
                            }
                          }}
                          className="bg-white"
                        />
                        {order.earliest_fitting_date && (
                          <div className="mt-2">
                            {(() => {
                              const fittingDate = new Date(order.earliest_fitting_date);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              fittingDate.setHours(0, 0, 0, 0);
                              const daysUntil = Math.ceil((fittingDate - today) / (1000 * 60 * 60 * 24));
                              
                              if (daysUntil <= 0) {
                                return (
                                  <p className="text-sm font-semibold text-green-700 bg-green-100 p-2 rounded">
                                    ✅ READY TO FIT! Restriction date has passed.
                                  </p>
                                );
                              } else if (daysUntil <= 14) {
                                return (
                                  <p className="text-sm text-amber-700 bg-amber-100 p-2 rounded">
                                    ⏳ {daysUntil} day{daysUntil !== 1 ? 's' : ''} until fitting allowed
                                  </p>
                                );
                              } else {
                                return (
                                  <p className="text-xs text-purple-700">
                                    Fitting allowed from: {fittingDate.toLocaleDateString('en-GB', { 
                                      weekday: 'short', 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </p>
                                );
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                <StatusBadge status={order.status} />
                {order.invoiced_waiting_payment && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 ml-2"
                    data-testid="invoiced-waiting-payment-badge"
                    title={`Invoice sent ${order.invoiced_waiting_payment_at ? 'on ' + new Date(order.invoiced_waiting_payment_at).toLocaleDateString() : ''}. Auto-clears when payment is recorded.`}
                  >
                    Invoiced — Waiting Payment
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {order.order_type !== 'grave vault' && order.order_type !== 'maintenance' && (
              <>
              {/* Memorial Type — new field driving the Corel-backed shape filter */}
              <div>
                <p className="text-sm text-slate-500">Memorial Type</p>
                <p className="font-semibold">
                  {order.memorial_type === 'cremation' ? 'Cremation Memorial'
                    : order.memorial_type === 'full' ? 'Full Size Memorial'
                    : <span className="text-slate-400 italic">Not set</span>}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stone Shape</p>
                <p className="font-semibold">{order.stone_shape || <span className="text-slate-400 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stone Type</p>
                <p className="font-semibold">{order.stone_type || <span className="text-slate-400 italic">—</span>}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stone Colour</p>
                <p className="font-semibold">{order.stone_colour}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stone Code <span className="text-xs text-slate-400">(optional)</span></p>
                <p className="font-semibold">{order.stone_code || <span className="text-slate-400 italic">—</span>}</p>
              </div>
              {order.kerb_surround && (
                <div data-testid="kerb-surround-detail">
                  <p className="text-sm text-slate-500">Kerb Surround</p>
                  <p className="font-semibold">
                    {[order.kerb_length && `L: ${order.kerb_length}`, order.kerb_width && `W: ${order.kerb_width}`, order.kerb_depth && `D: ${order.kerb_depth}`].filter(Boolean).join(' × ') || 'Yes'}
                  </p>
                </div>
              )}
              {order.cover_slab && (
                <div data-testid="cover-slab-detail">
                  <p className="text-sm text-slate-500">Cover Slab</p>
                  <p className="font-semibold">
                    {[order.cover_slab_length && `L: ${order.cover_slab_length}`, order.cover_slab_width && `W: ${order.cover_slab_width}`, order.cover_slab_depth && `D: ${order.cover_slab_depth}`].filter(Boolean).join(' × ') || 'Yes'}
                  </p>
                </div>
              )}
              </>
              )}
              <div>
                <p className="text-sm text-slate-500">Cemetery</p>
                <p className="font-semibold">{order.cemetery}</p>
              </div>
              {order.plot_number && (
                <div>
                  <p className="text-sm text-slate-500">Plot Number</p>
                  <p className="font-semibold">{order.plot_number}</p>
                </div>
              )}
              {/* Grave Vault specific fields */}
              {order.order_type === 'grave vault' && (
                <>
                  {order.vault_material && (
                    <div>
                      <p className="text-sm text-slate-500">Vault Material</p>
                      <p className="font-semibold capitalize">{order.vault_material}</p>
                    </div>
                  )}
                  {order.vault_availability && (
                    <div>
                      <p className="text-sm text-slate-500">Availability</p>
                      <p className="font-semibold capitalize">{order.vault_availability.replace(/-/g, ' ')}</p>
                    </div>
                  )}
                  {order.vault_size && (
                    <div>
                      <p className="text-sm text-slate-500">External Size</p>
                      <p className="font-semibold">{order.vault_size}</p>
                    </div>
                  )}
                  {order.date_required && (
                    <div>
                      <p className="text-sm text-slate-500">Date Required</p>
                      <p className="font-semibold">{new Date(order.date_required).toLocaleDateString('en-GB')}</p>
                    </div>
                  )}
                  {order.service_time && (
                    <div>
                      <p className="text-sm text-slate-500">Time of Service</p>
                      <p className="font-semibold">{order.service_time}</p>
                    </div>
                  )}
                  {order.lid_fitted_by && (
                    <div>
                      <p className="text-sm text-slate-500">Lid Fitted By</p>
                      <p className="font-semibold">{order.lid_fitted_by}</p>
                    </div>
                  )}
                </>
              )}
              {canSeeMoney && (
              <>
              {safeNumber(order.cemetery_fee) > 0 && (
                <div>
                  <p className="text-sm text-slate-500">Cemetery Fee (VAT Exempt)</p>
                  <p className="font-semibold text-amber-600">£{safeNumber(order.cemetery_fee).toFixed(2)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">VAT Rate</p>
                <p className="font-semibold">{safeNumber(order.vat_rate, 20)}%</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stone Price</p>
                <p className="font-semibold">£{safeNumber(order.price).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total (inc VAT)</p>
                <p className="font-bold text-blue-600 text-xl">£{totalAmount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Balance</p>
                <p className="font-bold text-xl" style={{ color: balance > 0 ? '#dc2626' : '#16a34a' }}>
                  {balance <= 0 ? 'PAID ✓' : `£${balance.toFixed(2)}`}
                </p>
              </div>
              </>
              )}
            </div>
            
            {/* Inscription Section - handles both regular and book style */}
            {(order.inscription || order.is_book_style || order.existing_inscription || order.new_inscription) && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  Inscription {order.is_book_style && <span className="text-amber-600">(Book Style)</span>}
                </p>
                
                {/* Existing Name - only for add on inscription orders */}
                {order.order_type === 'add on inscription' && order.existing_name && (
                  <div className="mb-3 p-2 bg-indigo-100 border border-indigo-300 rounded">
                    <p className="text-sm font-semibold text-indigo-900">Existing Name on Memorial</p>
                    <p className="text-indigo-800">{order.existing_name}</p>
                  </div>
                )}
                
                {/* Add-On Inscription - Show existing and new inscription separately */}
                {order.order_type === 'add on inscription' && (order.existing_inscription || order.new_inscription) ? (
                  <div className="space-y-3">
                    {/* Existing Inscription - Not Charged */}
                    {order.existing_inscription && (
                      <div className="p-3 bg-slate-100 border border-slate-300 rounded">
                        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-slate-500 text-white text-xs rounded">EXISTING</span>
                          Current Inscription on Memorial (Reference Only)
                        </p>
                        <p className="text-slate-700 whitespace-pre-wrap font-serif text-lg text-center">{order.existing_inscription}</p>
                      </div>
                    )}
                    
                    {/* New Inscription - Charged */}
                    {order.new_inscription && (
                      <div className="p-3 bg-green-50 border border-green-300 rounded">
                        <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded">NEW</span>
                          New Inscription to Add (Charged)
                        </p>
                        <p className="text-green-900 whitespace-pre-wrap font-serif text-lg text-center">{order.new_inscription}</p>
                      </div>
                    )}
                  </div>
                ) : order.is_book_style ? (
                  /* Book Style - Left and Right Pages */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-white border border-blue-200 rounded">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Left Page</p>
                      <p className="text-slate-800 whitespace-pre-wrap font-serif text-lg text-center">{order.inscription_left || '(No text)'}</p>
                    </div>
                    <div className="p-3 bg-white border border-blue-200 rounded">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Right Page</p>
                      <p className="text-slate-800 whitespace-pre-wrap font-serif text-lg text-center">{order.inscription_right || '(No text)'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-800 whitespace-pre-wrap font-serif text-lg text-center">{order.inscription}</p>
                )}
                
                {order.leave_space_for_additional && (
                  <div className="mt-3 p-2 bg-amber-100 border border-amber-300 rounded">
                    <p className="text-sm font-semibold text-amber-900">⚠️ Leave room for additional inscription</p>
                    <p className="text-xs text-amber-800">Space needed for future dates or names</p>
                  </div>
                )}
                
                {order.lettering_type && (
                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                    <p className="text-sm font-semibold text-purple-900">Lettering Details</p>
                    <div className="flex justify-between mt-2">
                      <div>
                        <p className="text-xs text-purple-700">Type</p>
                        <p className="text-sm font-medium capitalize">{order.lettering_type.replace(/_/g, ' ')}</p>
                      </div>
                      {safeNumber(order.lettering_cost) > 0 && (
                        <div>
                          <p className="text-xs text-purple-700">Extra Lettering Cost</p>
                          <p className="text-sm font-semibold">£{safeNumber(order.lettering_cost).toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {(order.font_style || order.font_colour || order.ornament || order.vase_holes) && (
                  <div className="mt-3 pt-3 border-t border-blue-200 grid grid-cols-3 gap-4">
                    {order.font_style && (
                      <div>
                        <p className="text-xs text-blue-700 font-medium">Font Style</p>
                        <p className="text-sm text-slate-700 capitalize">
                          {order.font_style === 'custom' && order.custom_font 
                            ? order.custom_font 
                            : order.font_style.replace(/-/g, ' ')}
                        </p>
                      </div>
                    )}
                    {order.font_colour && (
                      <div>
                        <p className="text-xs text-blue-700 font-medium">Font Colour</p>
                        <p className="text-sm text-slate-700 capitalize">
                          {order.font_colour === 'custom' && order.custom_font_colour 
                            ? order.custom_font_colour 
                            : order.font_colour.replace(/-/g, ' ')}
                        </p>
                      </div>
                    )}
                    {order.ornament && (
                      <div>
                        <p className="text-xs text-blue-700 font-medium">Ornament</p>
                        <p className="text-sm text-slate-700 capitalize">{order.ornament.replace(/-/g, ' ')}</p>
                      </div>
                    )}
                    {order.vase_holes && order.vase_holes !== 'none' && (
                      <div>
                        <p className="text-xs text-blue-700 font-medium">Vase Holes</p>
                        <p className="text-sm text-slate-700 capitalize">{order.vase_holes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {order.notes && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Notes</p>
                <p className="text-slate-700">{order.notes}</p>
              </div>
            )}
            
            {/* Maintenance Work Details - only for maintenance orders */}
            {order.order_type === 'maintenance' && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  Maintenance Work Required
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {order.maintenance_repaint_lettering && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Repaint Lettering</span>
                  )}
                  {order.maintenance_relevelling && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Re-levelling</span>
                  )}
                  {order.maintenance_paint_image && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Paint Image/Ornament</span>
                  )}
                  {order.maintenance_cleaning && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Cleaning</span>
                  )}
                  {order.maintenance_repair && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Repair Work</span>
                  )}
                  {order.maintenance_other && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Other</span>
                  )}
                </div>
                {order.maintenance_details && (
                  <div>
                    <p className="text-xs text-orange-700 font-medium mb-1">Work Details</p>
                    <p className="text-sm text-orange-900">{order.maintenance_details}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Memorial Collection Info - for add-on inscription and restoration orders */}
            {isCollectionOrderType && collectionRecord && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Memorial Collected
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePreviewCollectionEmail}
                    className={collectionRecord.email_sent ? "text-amber-700 hover:bg-amber-100" : "text-blue-700 hover:bg-blue-100"}
                  >
                    <Send className="w-3 h-3 mr-1" />
                    {collectionRecord.email_sent ? 'Preview & Resend' : 'Preview & Send Email'}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-amber-700 font-medium">Name on Stone</p>
                    <p className="text-sm text-amber-900">{collectionRecord.name_on_stone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-700 font-medium">Location</p>
                    <p className="text-sm text-amber-900">{collectionRecord.location}</p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-700 font-medium">Collected By</p>
                    <p className="text-sm text-amber-900">{collectionRecord.collected_by}</p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-700 font-medium">Collection Date</p>
                    <p className="text-sm text-amber-900">
                      {new Date(collectionRecord.collected_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-amber-700 font-medium">Memorial Details</p>
                  <p className="text-sm text-amber-900">{collectionRecord.memorial_details}</p>
                </div>
                {collectionRecord.distinguishing_features && (
                  <div className="mb-3">
                    <p className="text-xs text-amber-700 font-medium">Distinguishing Features</p>
                    <p className="text-sm text-amber-900">{collectionRecord.distinguishing_features}</p>
                  </div>
                )}
                {collectionRecord.photo_url && (
                  <div>
                    <p className="text-xs text-amber-700 font-medium mb-2">Photo Before Collection</p>
                    <img 
                      src={collectionRecord.photo_url} 
                      alt="Memorial before collection" 
                      className="max-w-xs rounded-lg border border-amber-300 cursor-pointer hover:opacity-90"
                      onClick={() => {
                        setFullSizeImage(collectionRecord.photo_url);
                        setFullSizeImageDialog(true);
                      }}
                    />
                  </div>
                )}
                {collectionRecord.email_sent && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-green-700">
                    <CheckCircle className="w-3 h-3" />
                    Client notified via email
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Allocation Section - hidden for add-on inscription, restoration, and maintenance orders */}
        {isAdmin && !['add on inscription', 'restoration', 'maintenance'].includes(order?.order_type) && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Boxes className="w-5 h-5 text-blue-600" />
                  Stock Allocation
                </CardTitle>
                {!allocatedStock && matchingStock.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={() => setShowStockDialog(true)}
                    data-testid="view-matching-stock-btn"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    View Matching Stock ({matchingStock.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {allocatedStock ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-lg font-bold bg-green-200 text-green-900 px-3 py-1 rounded">
                            {allocatedStock.stock_ref || 'N/A'}
                          </span>
                          <span className="text-green-800 font-semibold">Stock Allocated</span>
                        </div>
                        <p className="text-sm text-green-700">
                          {allocatedStock.memorial_type} - {allocatedStock.stone_type} ({allocatedStock.stone_colour || allocatedStock.stone_color})
                          {allocatedStock.stone_finish && ` - ${allocatedStock.stone_finish}`}
                        </p>
                        {allocatedStock.stone_code && (
                          <p className="text-xs text-green-600 mt-1">
                            Stone Code: <span className="font-mono">{allocatedStock.stone_code}</span>
                          </p>
                        )}
                        {allocatedStock.selling_price > 0 && (
                          <p className="text-sm text-green-700 font-medium mt-1">
                            Value: £{allocatedStock.selling_price?.toFixed(2)}
                          </p>
                        )}
                        {(allocatedStock.is_damaged || allocatedStock.requires_work) && (
                          <div className="flex gap-2 mt-2">
                            {allocatedStock.is_damaged && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Damaged
                              </Badge>
                            )}
                            {allocatedStock.requires_work && (
                              <Badge className="text-xs bg-amber-100 text-amber-800">
                                <Wrench className="w-3 h-3 mr-1" />
                                Needs Work
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleDeallocateStock}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Deallocate
                    </Button>
                  </div>
                </div>
              ) : matchingStock.length > 0 ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Boxes className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-800">Matching Stock Available!</p>
                      <p className="text-sm text-blue-700">
                        {matchingStock.length} item{matchingStock.length > 1 ? 's' : ''} in stock match this order's stone requirements.
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="mt-3 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowStockDialog(true)}
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Allocate Stock
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                  <Boxes className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">No stock allocated to this order</p>
                  <p className="text-sm text-slate-500">No matching items found in inventory</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stock Selection Dialog */}
        <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Select Stock to Allocate</DialogTitle>
              <DialogDescription>
                Choose a stock item to allocate to order #{order?.order_number}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {matchingStock.map((stock) => (
                <div 
                  key={stock.id}
                  className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-slate-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{stock.memorial_type}</p>
                      {stock.match_score >= 15 && (
                        <Badge className="bg-green-100 text-green-800 text-xs">Best Match</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {stock.stone_type} - {stock.stone_colour}
                    </p>
                    {stock.dimensions?.width && (
                      <p className="text-xs text-slate-500">
                        Dimensions: {stock.dimensions.width}" × {stock.dimensions.height}"
                        {stock.dimensions.depth && ` × ${stock.dimensions.depth}"`}
                      </p>
                    )}
                    <div className="flex gap-2 mt-1">
                      {stock.is_damaged && (
                        <Badge variant="destructive" className="text-xs">Damaged</Badge>
                      )}
                      {stock.requires_work && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">Needs Work</Badge>
                      )}
                      <span className="text-xs text-slate-500">
                        Selling: £{stock.selling_price?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleAllocateStock(stock.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Allocate
                  </Button>
                </div>
              ))}
              {matchingStock.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No matching stock items found
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Customer Agreement (Phase 2/3) — hidden entirely if not required */}
        {isStaffOrAdmin && <AgreementSection orderId={orderId} isAdmin={isAdmin} />}

        {/* Memorial Application System — shared cemetery database + permit applications */}
        {isStaffOrAdmin && <MemorialApplicationSection orderId={orderId} order={order} onOrderUpdate={fetchOrderData} />}

        {/* Tabs for different sections */}
        <Tabs defaultValue={canSeeMoney ? 'payments' : (order?.order_type !== 'maintenance' ? 'artwork' : 'attachments')} className="space-y-4">
          <TabsList className={`grid w-full bg-white shadow-md ${order?.order_type === 'maintenance' ? (canSeeMoney ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-2') : (canSeeMoney ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-3 sm:grid-cols-3')}`}>
            {canSeeMoney && (
            <>
            <TabsTrigger value="payments" data-testid="payments-tab" className="text-xs sm:text-sm">
              <PoundSterling className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Payments</span>
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="quotes-tab" className="text-xs sm:text-sm">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Quotes</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="invoices-tab" className="text-xs sm:text-sm">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Invoices</span>
            </TabsTrigger>
            </>
            )}
            {order?.order_type !== 'maintenance' && (
              <TabsTrigger value="artwork" data-testid="artwork-tab" className="text-xs sm:text-sm">
                <Image className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Artwork</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="attachments" data-testid="attachments-tab" className="text-xs sm:text-sm">
              <Paperclip className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Files</span>
              {attachments.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{attachments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="timeline" data-testid="timeline-tab" className="text-xs sm:text-sm">
              <Clock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Timeline</span>
            </TabsTrigger>
          </TabsList>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Payments</CardTitle>
                  {(isStaffOrAdmin) && (
                    <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-payment-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Payment
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Record Payment</DialogTitle>
                          <DialogDescription>Add a manual payment record</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddPayment} className="space-y-4" data-testid="payment-form">
                          <div className="space-y-2">
                            <Label htmlFor="payment-amount">Amount</Label>
                            <Input
                              id="payment-amount"
                              type="number"
                              step="0.01"
                              value={paymentForm.amount}
                              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                              required
                              data-testid="payment-amount-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="payment-method">Payment Method</Label>
                            <Input
                              id="payment-method"
                              value={paymentForm.payment_method}
                              onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                              placeholder="Cash, Card, Bank Transfer, etc."
                              required
                              data-testid="payment-method-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="payment-notes">Notes</Label>
                            <Textarea
                              id="payment-notes"
                              value={paymentForm.notes}
                              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                              data-testid="payment-notes-input"
                            />
                          </div>
                          <Button type="submit" className="w-full" data-testid="submit-payment-btn">Record Payment</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No payments recorded</p>
                ) : (
                  <div className="space-y-3" data-testid="payments-list">
                    {payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg" data-testid={`payment-${payment.id}`}>
                        <div>
                          <p className="font-semibold">£{safeNumber(payment.amount).toFixed(2)}</p>
                          <p className="text-sm text-slate-500">{payment.payment_method}</p>
                          {payment.notes && <p className="text-sm text-slate-600 mt-1">{payment.notes}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-sm text-slate-500">
                            {new Date(payment.created_at).toLocaleDateString('en-GB')}
                          </p>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditPayment(payment)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Quotes</CardTitle>
                  {(isStaffOrAdmin) && (
                    <Dialog open={quoteDialog} onOpenChange={(open) => {
                      setQuoteDialog(open);
                      if (open) populateQuoteFromOrder();
                    }}>
                      <DialogTrigger asChild>
                        <Button data-testid="create-quote-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Quote
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Create Quote</DialogTitle>
                          <DialogDescription>Generate a quote for the client</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateQuote} className="space-y-4" data-testid="quote-form">
                          <div className="space-y-3">
                            <Label>Quote Items</Label>
                            {quoteForm.items.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  placeholder="Description"
                                  value={item.description}
                                  onChange={(e) => updateQuoteItem(index, 'description', e.target.value)}
                                  required
                                  className="flex-1"
                                  data-testid={`quote-item-desc-${index}`}
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Amount"
                                  value={item.amount}
                                  onChange={(e) => updateQuoteItem(index, 'amount', e.target.value)}
                                  required
                                  className="w-32"
                                  data-testid={`quote-item-amount-${index}`}
                                />
                                {quoteForm.items.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => removeQuoteItem(index)}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button type="button" variant="outline" onClick={addQuoteItem} className="w-full" data-testid="add-quote-item-btn">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Item (Plus VAT @ {safeNumber(order.vat_rate, 20)}%)
                            </Button>
                            {safeNumber(order.cemetery_fee) > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                                <p className="text-sm font-semibold text-amber-900">VAT Exempt Item:</p>
                                <p className="text-sm text-amber-700">Cemetery/Church Fee: £{safeNumber(order.cemetery_fee).toFixed(2)}</p>
                                <p className="text-xs text-amber-600 mt-1">This will be added to the quote automatically (no VAT)</p>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="quote-notes">Notes</Label>
                            <Textarea
                              id="quote-notes"
                              value={quoteForm.notes}
                              onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                              data-testid="quote-notes-input"
                            />
                          </div>
                          <Button type="submit" className="w-full" data-testid="submit-quote-btn">Create Quote</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No quotes created</p>
                ) : (
                  <div className="space-y-4" data-testid="quotes-list">
                    {quotes.map((quote) => (
                      <div key={quote.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`quote-${quote.id}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-lg">£{quote.total.toFixed(2)}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(quote.created_at).toLocaleDateString('en-GB')}
                            </p>
                          </div>
                          <div className="flex gap-2 items-center">
                            {(isStaffOrAdmin) && !quote.sent_at && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handlePreviewQuote(quote.id)} data-testid={`preview-quote-${quote.id}`}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Preview
                                </Button>
                                <Button size="sm" onClick={() => handleSendQuote(quote.id)} data-testid={`send-quote-${quote.id}`}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Send
                                </Button>
                              </>
                            )}
                            {quote.sent_at && (
                              <Badge variant="secondary">Sent {new Date(quote.sent_at).toLocaleDateString()}</Badge>
                            )}
                            {(isStaffOrAdmin) && (
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteQuote(quote.id)} 
                                data-testid={`delete-quote-${quote.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Items (Plus VAT @ {quote.vat_rate}%):</p>
                          {quote.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.description}</span>
                              <span>£{item.amount.toFixed(2)}</span>
                            </div>
                          ))}
                          {quote.vat_exempt_items && quote.vat_exempt_items.length > 0 && (
                            <>
                              <p className="text-sm font-semibold text-amber-700 mt-3 mb-2">VAT Exempt Items:</p>
                              {quote.vat_exempt_items.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-amber-700">
                                  <span>{item.description}</span>
                                  <span>£{item.amount.toFixed(2)}</span>
                                </div>
                              ))}
                            </>
                          )}
                          <div className="border-t pt-2 mt-2">
                            <div className="flex justify-between text-sm">
                              <span>Subtotal (Taxable)</span>
                              <span>£{quote.subtotal.toFixed(2)}</span>
                            </div>
                            {quote.vat_exempt_total > 0 && (
                              <div className="flex justify-between text-sm text-amber-700">
                                <span>VAT Exempt Items</span>
                                <span>£{quote.vat_exempt_total.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span>VAT @ {quote.vat_rate}%</span>
                              <span>£{quote.tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-semibold">
                              <span>Total</span>
                              <span>£{quote.total.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Invoices</CardTitle>
                  {(isStaffOrAdmin) && (
                    <Dialog open={invoiceDialog} onOpenChange={(open) => {
                      setInvoiceDialog(open);
                      if (open) populateInvoiceAmount();
                    }}>
                      <DialogTrigger asChild>
                        <Button data-testid="create-invoice-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Invoice
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Invoice</DialogTitle>
                          <DialogDescription>
                            Invoice for balance due (Total - Payments Made)
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateInvoice} className="space-y-4" data-testid="invoice-form">
                          <div className="space-y-2">
                            <Label htmlFor="invoice-amount">Balance Due</Label>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-slate-600 mb-1">Outstanding Balance (Auto-calculated):</p>
                              <p className="text-3xl font-bold text-blue-600">£{invoiceForm.amount}</p>
                            </div>
                            <p className="text-xs text-slate-500">
                              Includes: Stone Price + Sundries + VAT ({safeNumber(order?.vat_rate, 20)}%) + Cemetery Fee - All Payments
                            </p>
                          </div>
                          {quotes.length > 0 && (
                            <div className="space-y-2">
                              <Label>Linked Quote</Label>
                              <p className="text-sm text-slate-600">This invoice is based on Quote #{quotes[quotes.length - 1].id.slice(0, 8)}</p>
                            </div>
                          )}
                          <Button type="submit" className="w-full" data-testid="submit-invoice-btn">Create Invoice</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No invoices created</p>
                ) : (
                  <div className="space-y-3" data-testid="invoices-list">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg" data-testid={`invoice-${invoice.id}`}>
                        <div>
                          <p className="font-semibold text-lg">£{invoice.amount.toFixed(2)}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(invoice.created_at).toLocaleDateString('en-GB')}
                          </p>
                          <Badge className={`badge status-${invoice.status} mt-2`}>{invoice.status}</Badge>
                        </div>
                        <div className="flex gap-2">
                          {isAdmin && invoice.status !== 'paid' && (
                            <Button 
                              size="sm" 
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setMarkPaidDialog(true);
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Paid
                            </Button>
                          )}
                          {(isStaffOrAdmin) && !invoice.sent_at && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handlePreviewInvoice(invoice.id)} data-testid={`preview-invoice-${invoice.id}`}>
                                <FileText className="w-4 h-4 mr-2" />
                                Preview
                              </Button>
                              <Button size="sm" onClick={() => handleSendInvoice(invoice.id)} data-testid={`send-invoice-${invoice.id}`}>
                                <Send className="w-4 h-4 mr-2" />
                                Send
                              </Button>
                            </>
                          )}
                          {(isStaffOrAdmin) && invoice.sent_at && (
                            <>
                              <Badge variant="secondary" className="mr-2">Sent {new Date(invoice.sent_at).toLocaleDateString()}</Badge>
                              <Button size="sm" variant="outline" onClick={() => handlePreviewInvoice(invoice.id)}>
                                <FileText className="w-4 h-4 mr-2" />
                                View Invoice
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDownloadInvoicePDF(invoice.id)}>
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </Button>
                            </>
                          )}
                          {(isStaffOrAdmin) && (
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => handleDeleteInvoice(invoice.id)} 
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Artwork Tab - hidden for maintenance orders */}
          {order?.order_type !== 'maintenance' && (
          <TabsContent value="artwork">
            <DesignerServicePanel orderId={orderId} token={token} currentUser={user} />
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Artwork</CardTitle>
                    {artwork.length > 0 && (
                      <p className="text-sm text-slate-500 mt-1">
                        {artwork.filter(a => a.status === 'approved').length} of {artwork.length} approved
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(isStaffOrAdmin) && artwork.filter(a => a.status === 'pending' && !a.sent_at).length > 0 && (
                      <Button 
                        variant="outline" 
                        onClick={handleSendAllArtwork}
                        disabled={sendingAllArtwork}
                        data-testid="send-all-artwork-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {sendingAllArtwork ? 'Sending...' : `Send All (${artwork.filter(a => a.status === 'pending' && !a.sent_at).length})`}
                      </Button>
                    )}
                    {(isStaffOrAdmin) && (
                      <Dialog open={artworkDialog} onOpenChange={(open) => {
                        setArtworkDialog(open);
                        if (!open) {
                          setArtworkFile(null);
                          setArtworkLabel('');
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button data-testid="upload-artwork-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Artwork
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Upload Artwork</DialogTitle>
                            <DialogDescription>Add artwork for client review. Multiple pieces can be uploaded and sent together.</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleUploadArtwork} className="space-y-4" data-testid="artwork-form">
                            <div className="space-y-2">
                              <Label htmlFor="artwork-label">Label (Optional)</Label>
                              <Input
                                id="artwork-label"
                                type="text"
                                placeholder="e.g., Front Design, Back Design, Vase, Side Panel"
                                value={artworkLabel}
                                onChange={(e) => setArtworkLabel(e.target.value)}
                                disabled={uploadingArtwork}
                                data-testid="artwork-label-input"
                              />
                              <p className="text-xs text-slate-500">
                                Give each artwork a descriptive name so clients know what they&apos;re approving
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="artwork-file">Select File</Label>
                              <Input
                                id="artwork-file"
                                type="file"
                                onChange={(e) => setArtworkFile(e.target.files[0])}
                                required
                                accept="image/*,.pdf,application/pdf"
                                data-testid="artwork-file-input"
                                disabled={uploadingArtwork}
                                className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-600 file:text-white hover:file:bg-slate-700 file:cursor-pointer"
                              />
                              <p className="text-xs text-slate-500 mt-1">Accepts: PNG, JPG, GIF, WebP, PDF (max 12MB)</p>
                              {artworkFile && (
                                <div className="p-2 bg-green-50 border border-green-200 rounded">
                                  <p className="text-sm text-green-700 font-semibold">✓ Selected: {artworkFile.name}</p>
                                  <p className="text-xs text-green-600">
                                    Size: {(artworkFile.size / 1024).toFixed(2)} KB | Type: {artworkFile.type || 'unknown'}
                                  </p>
                                </div>
                              )}
                            </div>
                            <Button 
                              type="submit" 
                              className="w-full" 
                              data-testid="submit-artwork-btn"
                              disabled={uploadingArtwork}
                            >
                              {uploadingArtwork ? 'Uploading...' : 'Upload Artwork'}
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {artwork.length === 0 ? (
                  artworkLoadFailed ? (
                    <div className="p-4 rounded-lg border border-red-300 bg-red-50 flex items-center justify-between flex-wrap gap-2" data-testid="artwork-load-error">
                      <p className="text-sm text-red-800 font-medium">Artwork could not be loaded (server timeout) — your artwork is safe, it just failed to load.</p>
                      <Button size="sm" variant="outline" className="border-red-400 text-red-700" onClick={() => fetchOrderData()}>Retry</Button>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No artwork uploaded</p>
                  )
                ) : (
                  <div className="space-y-4">
                    {/* Current Artwork */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="artwork-list">
                      {artwork.map((art, idx) => (
                        <div key={art.id} className={`p-4 rounded-lg space-y-3 ${
                          art.status === 'approved' ? 'bg-green-50 border border-green-200' :
                          art.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                          'bg-slate-50'
                        }`} data-testid={`artwork-${art.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold">{art.label || `Artwork ${idx + 1}`}</p>
                              <p className="text-sm text-slate-500">
                                {art.filename} • v{art.version || 1} • {new Date(art.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={`badge status-${art.status}`}>{art.status}</Badge>
                          </div>
                          
                          {!art.file_data && (
                            <div className="w-full h-64 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center animate-pulse" data-testid={`artwork-loading-${art.id}`}>
                              <p className="text-sm text-slate-400">Loading preview…</p>
                            </div>
                          )}
                          {art.file_data && (
                            <>
                              {/* Check if it's a PDF */}
                              {(art.content_type === 'application/pdf' || art.filename?.toLowerCase().endsWith('.pdf')) ? (
                                <div className="relative">
                                  <div className="w-full h-64 bg-slate-100 border border-slate-200 rounded-lg flex flex-col items-center justify-center">
                                    <FileText className="w-16 h-16 text-red-500 mb-2" />
                                    <p className="text-sm font-medium text-slate-700">{art.filename}</p>
                                    <p className="text-xs text-slate-500">PDF Document</p>
                                  </div>
                                  <div className="flex gap-2 mt-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => {
                                        // Open PDF in new tab
                                        const pdfData = `data:application/pdf;base64,${art.file_data}`;
                                        const newWindow = window.open();
                                        newWindow.document.write(`<iframe src="${pdfData}" style="width:100%;height:100vh;border:none;"></iframe>`);
                                      }}
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      View PDF
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => {
                                        // Download PDF
                                        const link = document.createElement('a');
                                        link.href = `data:application/pdf;base64,${art.file_data}`;
                                        link.download = art.filename || 'artwork.pdf';
                                        link.click();
                                      }}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  className="relative cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => {
                                    const mimeType = art.content_type || 'image/jpeg';
                                    setFullSizeImage({
                                      src: `data:${mimeType};base64,${art.file_data}`,
                                      filename: art.filename,
                                      version: art.version || 1
                                    });
                                    setFullSizeImageDialog(true);
                                  }}
                                >
                                  <img
                                    src={`data:${art.content_type || 'image/jpeg'};base64,${art.file_data}`}
                                    alt={art.filename}
                                    className="w-full h-64 object-contain bg-white border border-slate-200 rounded-lg"
                                  />
                                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                    Click to enlarge
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          
                          <div className="flex flex-wrap gap-2">
                            {(isStaffOrAdmin) && art.status === 'pending' && !art.sent_at && (
                              <Button size="sm" onClick={() => handlePreviewArtwork(art)} className="flex-1" data-testid={`send-artwork-${art.id}`}>
                                <Send className="w-4 h-4 mr-2" />
                                Send to Client
                              </Button>
                            )}
                            
                            {/* Approve Artwork button - show for admin when artwork is pending (even after sent to client) */}
                            {isAdmin && art.status === 'pending' && (
                              <Button 
                                size="sm" 
                                onClick={async () => {
                                  if (!window.confirm('Approve this artwork? This will create a task for Trevor to generate church/cemetery application.')) return;
                                  try {
                                    await axios.post(`${API}/orders/${orderId}/approve-artwork`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('Artwork approved! Task created for Trevor.');
                                    fetchOrderData();
                                  } catch (error) {
                                    toast.error('Failed to approve artwork');
                                  }
                                }}
                                className="bg-green-600 hover:bg-green-700 flex-1"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve Artwork
                              </Button>
                            )}

                            {/* Manual Reject button — mirrors Approve for when
                                the client rings/emails in with change requests.
                                Opens the existing manualChangesDialog to
                                capture the notes and set status to
                                'changes-requested'. */}
                            {isAdmin && art.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setManualChangeNotes('');
                                  setStatusDate(new Date().toISOString().slice(0, 16));
                                  setManualChangesDialog(true);
                                }}
                                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                                data-testid={`manual-reject-artwork-${art.id}`}
                                title="Client rang in with changes? Log rejection with notes."
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            )}
                            
                            {/* Reset status back to awaiting approval — for when a
                                customer clicked Approve in their email by mistake */}
                            {isAdmin && ['approved', 'rejected', 'changes_requested', 'changes-requested'].includes(art.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  if (!window.confirm('Reset this artwork back to Awaiting Approval? The customer can then approve or request changes again.')) return;
                                  try {
                                    const res = await axios.post(`${API}/artwork/${art.id}/reset-status`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success(res.data.order_status_reverted
                                      ? 'Artwork reset to awaiting approval — order status reverted to Artwork Created'
                                      : 'Artwork reset to awaiting approval');
                                    fetchOrderData();
                                  } catch (error) {
                                    toast.error('Failed to reset artwork status');
                                  }
                                }}
                                className="border-slate-300 text-slate-600 hover:bg-slate-50"
                                data-testid={`reset-artwork-status-${art.id}`}
                                title="Customer approved by mistake? Reset back to awaiting approval."
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reset to Awaiting Approval
                              </Button>
                            )}

                            {/* Always show delete button for admin */}
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={async () => {
                                  if (!window.confirm('Delete this artwork?')) return;
                                  try {
                                    await axios.delete(`${API}/artwork/${art.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('Artwork deleted');
                                    fetchOrderData();
                                  } catch (error) {
                                    toast.error('Failed to delete artwork');
                                  }
                                }}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {user?.role === 'client' && art.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleArtworkAction(art.id, 'approved')}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                data-testid={`approve-artwork-${art.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleArtworkAction(art.id, 'rejected')}
                                className="flex-1"
                                data-testid={`reject-artwork-${art.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          )}
                          {art.sent_at && (
                            <Badge variant="secondary" className="flex-1 justify-center">
                              Sent {new Date(art.sent_at).toLocaleDateString()}
                            </Badge>
                          )}
                        </div>
                        {art.notes && (
                          <p className="text-sm text-slate-600 p-2 bg-white rounded">{art.notes}</p>
                        )}
                        
                        {/* Show change request details when status is changes_requested */}
                        {(art.status === 'changes_requested' || art.status === 'rejected') && art.rejection_notes && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                              <p className="text-sm font-semibold text-amber-800">
                                {art.rejection_source === 'manual' ? 'Requested Changes (Internal):' : "Customer's Requested Changes:"}
                              </p>
                            </div>
                            <p className="text-sm text-amber-900 whitespace-pre-wrap">{art.rejection_notes}</p>
                            {art.rejection_file_data && (
                              <div className="mt-3">
                                <p className="text-xs text-amber-700 mb-2">📎 Customer uploaded reference image:</p>
                                <img
                                  src={`data:image/jpeg;base64,${art.rejection_file_data}`}
                                  alt="Customer change request reference"
                                  className="max-w-xs rounded border border-amber-300 cursor-pointer hover:opacity-90"
                                  onClick={() => {
                                    setFullSizeImage({
                                      src: `data:image/jpeg;base64,${art.rejection_file_data}`,
                                      filename: art.rejection_file_name || 'change-request.jpg',
                                      version: 'Change Request'
                                    });
                                    setFullSizeImageDialog(true);
                                  }}
                                />
                              </div>
                            )}
                            {art.rejected_at && (
                              <p className="text-xs text-amber-600 mt-2">
                                Requested on {new Date(art.rejected_at).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Version History */}
                  {artworkVersions.length > 1 && (isAdmin || user?.role === 'staff' || user?.role === 'workshop') && (
                    <div className="mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowVersions(!showVersions)}
                        className="mb-3"
                      >
                        {showVersions ? 'Hide' : 'Show'} Version History ({artworkVersions.length - 1} archived)
                      </Button>
                      
                      {showVersions && (
                        <div className="space-y-3">
                          <h3 className="font-semibold text-slate-700">Archived Versions</h3>
                          {artworkVersions.filter(v => v.is_archived).map((version) => (
                            <div key={version.id} className="p-3 bg-slate-100 rounded-lg border border-slate-200">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-medium text-sm">{version.filename}</p>
                                  <p className="text-xs text-slate-500">
                                    Version {version.version} • Archived {new Date(version.archived_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <Badge variant="secondary">Archived</Badge>
                              </div>
                              
                              {version.file_data && (
                                <div 
                                  className="relative cursor-pointer hover:opacity-90 transition-opacity mb-2"
                                  onClick={() => {
                                    setFullSizeImage({
                                      src: `data:image/jpeg;base64,${version.file_data}`,
                                      filename: version.filename,
                                      version: version.version
                                    });
                                    setFullSizeImageDialog(true);
                                  }}
                                >
                                  <img
                                    src={`data:image/jpeg;base64,${version.file_data}`}
                                    alt={version.filename}
                                    className="w-full h-48 object-contain bg-white border border-slate-200 rounded"
                                  />
                                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                    Click to enlarge
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = `data:image/jpeg;base64,${version.file_data}`;
                                    link.download = version.filename;
                                    link.click();
                                  }}
                                  className="flex-1"
                                >
                                  Download
                                </Button>
                                {isAdmin && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleRestoreArtwork(version.id, version.version)}
                                      className="flex-1"
                                    >
                                      Restore
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={async () => {
                                        if (!window.confirm(`Delete version ${version.version}?`)) return;
                                        try {
                                          await axios.delete(`${API}/artwork/${version.id}`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          toast.success('Version deleted');
                                          fetchOrderData();
                                        } catch (error) {
                                          toast.error('Failed to delete version');
                                        }
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Attachments Tab */}
          <TabsContent value="attachments">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Attachments
                    {attachments.length > 0 && <Badge variant="secondary">{attachments.length} files</Badge>}
                  </CardTitle>
                  {isStaffOrAdmin && (
                    <div>
                      <input
                        type="file"
                        id="attachment-upload"
                        className="hidden"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.heic,.heif"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;
                          setUploadingAttachment(true);
                          try {
                            const formData = new FormData();
                            files.forEach(f => formData.append('files', f));
                            await axios.post(`${API}/orders/${orderId}/attachments`, formData, {
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
                            fetchOrderData();
                          } catch (err) {
                            let msg;
                            if (err.response?.status === 413) {
                              msg = 'File too large for the server to accept — please use files under 10MB';
                            } else {
                              const detail = err.response?.data?.detail;
                              msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : `Failed to upload files${err.response?.status ? ` (error ${err.response.status})` : ''}`);
                            }
                            toast.error(msg);
                          } finally {
                            setUploadingAttachment(false);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button
                        data-testid="upload-attachment-btn"
                        onClick={() => document.getElementById('attachment-upload').click()}
                        disabled={uploadingAttachment}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingAttachment ? 'Uploading...' : 'Upload Files'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {attachments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Paperclip className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>No attachments yet</p>
                    <p className="text-sm mt-1">Upload documents, photos, or other files related to this order</p>
                    <p className="text-xs mt-2 text-slate-400">Supported: PDF, Word, Excel, JPG, PNG, GIF, TXT, CSV (max 10MB each)</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {attachments.map((att) => {
                      const isImg = att.is_image;
                      const sizeKB = att.file_size ? (att.file_size / 1024).toFixed(0) : '?';
                      const sizeMB = att.file_size ? (att.file_size / (1024 * 1024)).toFixed(1) : null;
                      const displaySize = sizeMB && parseFloat(sizeMB) >= 1 ? `${sizeMB} MB` : `${sizeKB} KB`;
                      const previewSrc = att.data_url; // present for images stored in Mongo (base64) — undefined otherwise

                      const downloadAttachment = async () => {
                        if (att.missing) { toast.error('This file is no longer available. Please re-upload it.'); return; }
                        try {
                          const res = await axios.get(`${API}/orders/${orderId}/attachments/${att.id}/download`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob',
                          });
                          const url = window.URL.createObjectURL(res.data);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = att.original_name || 'download';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                        } catch (e) {
                          toast.error(e.response?.status === 410
                            ? 'This file is no longer available (lost before durable storage was enabled). Please re-upload it.'
                            : 'Download failed');
                        }
                      };

                      return (
                        <div key={att.id} className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow" data-testid={`attachment-${att.id}`}>
                          {/* Preview area */}
                          {isImg && previewSrc ? (
                            <div
                              className="h-36 bg-slate-100 flex items-center justify-center cursor-pointer overflow-hidden"
                              onClick={() => { setFullSizeImage(previewSrc); setFullSizeImageDialog(true); }}
                            >
                              <img src={previewSrc} alt={att.original_name} className="w-full h-full object-cover" />
                            </div>
                          ) : att.missing ? (
                            <div className="h-36 bg-red-50 flex flex-col items-center justify-center text-red-500" title="File missing on server — please re-upload">
                              <FileIcon className="w-10 h-10 mb-2 opacity-60" />
                              <span className="text-xs font-medium">File Missing</span>
                            </div>
                          ) : (
                            <div className="h-36 bg-slate-50 flex flex-col items-center justify-center">
                              <FileIcon className="w-10 h-10 text-slate-400 mb-2" />
                              <span className="text-xs font-mono text-slate-500 uppercase">{att.file_type?.replace('.','')}</span>
                            </div>
                          )}
                          {/* File info */}
                          <div className="p-3">
                            <p className="text-sm font-medium text-slate-800 truncate" title={att.original_name}>{att.original_name}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-slate-500">{displaySize} &middot; {new Date(att.uploaded_at).toLocaleDateString('en-GB')}</span>
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={downloadAttachment}
                                  disabled={att.missing}
                                  className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 w-8 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                  data-testid={`download-attachment-${att.id}`}
                                  title={att.missing ? 'File missing on server' : 'Download'}
                                >
                                  <Download className="w-3.5 h-3.5 text-slate-600" />
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Delete this attachment?')) return;
                                      try {
                                        await axios.delete(`${API}/orders/${orderId}/attachments/${att.id}`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        toast.success('Attachment deleted');
                                        fetchOrderData();
                                      } catch (err) {
                                        toast.error('Failed to delete attachment');
                                      }
                                    }}
                                    className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 w-8 border border-red-200 bg-white hover:bg-red-50"
                                    data-testid={`delete-attachment-${att.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">by {att.uploaded_by}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Order Timeline</CardTitle>
                  <div className="flex gap-2">
                    {(isStaffOrAdmin) && (
                      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-event-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Event
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Timeline Event</DialogTitle>
                            <DialogDescription>Record calls, emails, notes, or other events</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleAddEvent} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="event-type">Event Type</Label>
                              <select
                                id="event-type"
                                value={eventForm.event_type}
                                onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              >
                                <option value="note">Note</option>
                                <option value="call">Phone Call</option>
                                <option value="email">Email</option>
                                <option value="general">General</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="event-title">Title</Label>
                              <Input
                                id="event-title"
                                placeholder="e.g., Client called regarding inscription"
                                value={eventForm.title}
                                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="event-description">Description (Optional)</Label>
                              <Textarea
                                id="event-description"
                                placeholder="Additional details..."
                                value={eventForm.description}
                                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                                rows={3}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="event-date">Date & Time</Label>
                              <Input
                                id="event-date"
                                type="datetime-local"
                                value={eventForm.event_date}
                                onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                                required
                              />
                            </div>
                            <Button type="submit" className="w-full">Add Event</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                    {timeline.length > 0 && (isStaffOrAdmin) && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={handleDeleteAllEvents}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove All Events
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No timeline events yet</p>
                ) : (
                  <div className="space-y-4">
                    {timeline.map((event) => (
                      <div key={event.id} className="flex gap-4 p-4 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex-shrink-0 w-24 text-sm text-slate-600">
                          <div className="font-semibold">
                            {new Date(event.event_date).toLocaleDateString('en-GB', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </div>
                          <div className="text-xs">
                            {new Date(event.event_date).toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={
                                  event.event_type === 'status_change' ? 'default' :
                                  event.event_type === 'call' ? 'secondary' :
                                  event.event_type === 'email' ? 'outline' :
                                  'secondary'
                                }>
                                  {event.event_type.replace('_', ' ')}
                                </Badge>
                                <h4 className="font-semibold text-slate-900">{event.title}</h4>
                              </div>
                              {event.description && (
                                <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-2">By {event.created_by}</p>
                            </div>
                            {(isStaffOrAdmin) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteEvent(event.id)}
                                title="Delete this event (will affect traffic lights)"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Modal */}
        <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview - {previewContent.subject}</DialogTitle>
              <DialogDescription>
                This is how the email will appear to the client
              </DialogDescription>
            </DialogHeader>
            <div className="border rounded-lg p-4 bg-white">
              <div className="mb-4 pb-4 border-b">
                <p className="text-sm text-slate-600"><strong>Subject:</strong> {previewContent.subject}</p>
                <p className="text-sm text-slate-600"><strong>To:</strong> {order?.client_email}</p>
              </div>
              <div 
                className="email-preview"
                dangerouslySetInnerHTML={{ __html: previewContent.body }}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setPreviewDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSend}>
                <Send className="w-4 h-4 mr-2" />
                Confirm & Send
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Artwork Preview Modal */}
        <Dialog open={artworkPreviewDialog} onOpenChange={setArtworkPreviewDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview - Artwork Ready to Send</DialogTitle>
              <DialogDescription>
                This is exactly how the email will appear to the client (with artwork image embedded)
              </DialogDescription>
            </DialogHeader>
            
            {selectedArtwork && selectedArtwork.emailPreview && (
              <div className="space-y-4">
                {/* Email Details */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="space-y-2 text-sm">
                    <p><strong>To:</strong> {selectedArtwork.emailPreview.to}</p>
                    <p><strong>Subject:</strong> {selectedArtwork.emailPreview.subject}</p>
                  </div>
                </div>

                {/* Actual Email Body Preview */}
                <div className="border-2 rounded-lg p-4 bg-white">
                  <div 
                    className="email-preview"
                    dangerouslySetInnerHTML={{ __html: selectedArtwork.emailPreview.body }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setArtworkPreviewDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => selectedArtwork && handleSendArtwork(selectedArtwork.id)} data-testid="confirm-send-artwork-btn" className="bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Confirm & Send Email
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Task Dialog */}
        <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Task to Order</DialogTitle>
              <DialogDescription>
                Create a new task for this order with optional timer and assignment
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="task-title">Task Title *</Label>
                <Input
                  id="task-title"
                  placeholder="e.g., Design memorial artwork"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="task-description">Description *</Label>
                <Textarea
                  id="task-description"
                  placeholder="Task details..."
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task-priority">Priority</Label>
                  <select
                    id="task-priority"
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="task-assigned">Assign To</Label>
                  <select
                    id="task-assigned"
                    value={taskForm.assigned_to}
                    onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select user...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task-timer">Timer Duration (Hours)</Label>
                  <Input
                    id="task-timer"
                    type="number"
                    min="1"
                    placeholder="e.g., 24, 48, 72"
                    value={taskForm.timer_duration_hours}
                    onChange={(e) => setTaskForm({ ...taskForm, timer_duration_hours: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">Task becomes urgent when timer expires</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="task-due">Due Date (Optional)</Label>
                  <Input
                    id="task-due"
                    type="date"
                    value={taskForm.due_date ? taskForm.due_date.split('T')[0] : ''}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setTaskDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Payment Dialog */}
        <Dialog open={editPaymentDialog} onOpenChange={setEditPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Payment</DialogTitle>
              <DialogDescription>Update payment details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdatePayment} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-amount">Amount (£)</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  value={editPaymentForm.amount}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-method">Payment Method</Label>
                <Input
                  id="edit-method"
                  value={editPaymentForm.payment_method}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_method: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes (Optional)</Label>
                <Textarea
                  id="edit-notes"
                  value={editPaymentForm.notes}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">Update Payment</Button>
                <Button type="button" variant="outline" onClick={() => setEditPaymentDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Mark Invoice Paid Dialog */}
        <Dialog open={markPaidDialog} onOpenChange={setMarkPaidDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Invoice as Paid</DialogTitle>
              <DialogDescription>
                This will mark the invoice as paid and create a payment record for £{selectedInvoice?.amount.toFixed(2)}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMarkInvoicePaid} className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-900">Payment Amount:</p>
                <p className="text-2xl font-bold text-green-700">£{selectedInvoice?.amount.toFixed(2)}</p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Payment
                </Button>
                <Button type="button" variant="outline" onClick={() => setMarkPaidDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Memorial Collection Dialog */}
        <Dialog open={collectionDialog} onOpenChange={setCollectionDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-amber-600" />
                Record Memorial Collection
              </DialogTitle>
              <DialogDescription>
                Record the collection of this memorial from the cemetery. The client will be emailed a confirmation.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitCollection} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="collection-name">Name on Stone *</Label>
                <Input
                  id="collection-name"
                  value={collectionForm.name_on_stone}
                  onChange={(e) => setCollectionForm({ ...collectionForm, name_on_stone: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                  data-testid="collection-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="collection-details">Memorial Details *</Label>
                <Textarea
                  id="collection-details"
                  value={collectionForm.memorial_details}
                  onChange={(e) => setCollectionForm({ ...collectionForm, memorial_details: e.target.value })}
                  placeholder="Describe the memorial (e.g., Black granite headstone, upright, approximately 3ft tall)"
                  rows={2}
                  required
                  data-testid="collection-details-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="collection-features">Distinguishing Features</Label>
                <Input
                  id="collection-features"
                  value={collectionForm.distinguishing_features}
                  onChange={(e) => setCollectionForm({ ...collectionForm, distinguishing_features: e.target.value })}
                  placeholder="Any unique features or identifying marks"
                  data-testid="collection-features-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="collection-location">Location *</Label>
                  <Input
                    id="collection-location"
                    value={collectionForm.location}
                    onChange={(e) => setCollectionForm({ ...collectionForm, location: e.target.value })}
                    placeholder="Cemetery name"
                    required
                    data-testid="collection-location-input"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="collection-by">Collected By *</Label>
                  <Input
                    id="collection-by"
                    value={collectionForm.collected_by}
                    onChange={(e) => setCollectionForm({ ...collectionForm, collected_by: e.target.value })}
                    placeholder="Staff name"
                    required
                    data-testid="collection-by-input"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="collection-photo">Photo of Memorial</Label>
                <Input
                  id="collection-photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCollectionPhoto(e.target.files?.[0] || null)}
                  data-testid="collection-photo-input"
                />
                {collectionPhoto && (
                  <p className="text-xs text-green-600">✓ {collectionPhoto.name}</p>
                )}
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t">
                <p className="text-xs text-slate-500">
                  {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setCollectionDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    size="sm"
                    disabled={submittingCollection}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {submittingCollection ? 'Saving...' : 'Save & Preview Email'}
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Collection Email Preview Dialog */}
        <Dialog open={collectionPreviewDialog} onOpenChange={setCollectionPreviewDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Email Preview
              </DialogTitle>
              <DialogDescription>
                Review the email before sending to {collectionEmailPreview?.to_email}
              </DialogDescription>
            </DialogHeader>
            
            {collectionEmailPreview && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-sm"><strong>To:</strong> {collectionEmailPreview.to_email}</p>
                  <p className="text-sm"><strong>Subject:</strong> {collectionEmailPreview.subject}</p>
                  {collectionEmailPreview.has_photo && (
                    <p className="text-sm text-green-600">📷 Photo will be attached</p>
                  )}
                </div>
                
                <div 
                  className="border rounded-lg p-4 bg-white max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: collectionEmailPreview.body }}
                />
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setCollectionPreviewDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSendCollectionEmail}
                    disabled={sendingCollectionEmail}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingCollectionEmail ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Full Size Image Dialog */}
        <Dialog open={fullSizeImageDialog} onOpenChange={setFullSizeImageDialog}>
          <DialogContent className="max-w-6xl w-full">
            <DialogHeader>
              <DialogTitle>
                {fullSizeImage?.filename} {fullSizeImage?.version && `- Version ${fullSizeImage.version}`}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center max-h-[80vh] overflow-auto bg-slate-50 rounded-lg p-4">
              {fullSizeImage && (
                <img
                  src={typeof fullSizeImage === 'string' ? fullSizeImage : fullSizeImage.src}
                  alt={typeof fullSizeImage === 'string' ? 'Memorial photo' : fullSizeImage.filename}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  if (fullSizeImage) {
                    const link = document.createElement('a');
                    link.href = typeof fullSizeImage === 'string' ? fullSizeImage : fullSizeImage.src;
                    link.download = typeof fullSizeImage === 'string' ? 'memorial_photo.jpg' : fullSizeImage.filename;
                    link.click();
                  }
                }}
              >
                Download
              </Button>
              <Button onClick={() => setFullSizeImageDialog(false)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Demo Email Dialog */}
        <Dialog open={demoEmailDialog} onOpenChange={setDemoEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Try the {demoEmailType} Email
              </DialogTitle>
              <DialogDescription>
                Experience how clients receive {demoEmailType === 'artwork' ? 'artwork approval requests' : demoEmailType === 'quote' ? 'quotes' : 'invoices'}! 
                Enter your email address below and we'll send you a real {demoEmailType} email with 
                {demoEmailType === 'artwork' ? ' approve/reject buttons' : ' all the details'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="demo-email">Your Email Address</Label>
                <Input
                  id="demo-email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  data-testid="demo-email-input"
                />
                <p className="text-xs text-slate-500">
                  {demoEmailType === 'artwork' 
                    ? "You'll receive the artwork with approve/reject buttons - click them to see how the workflow works!"
                    : `You'll receive a real ${demoEmailType} email just like your clients would.`}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDemoEmailDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendDemoEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-demo-email-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to My Email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Order Number Dialog */}
        <Dialog open={editOrderNumberDialog} onOpenChange={(open) => {
          setEditOrderNumberDialog(open);
          if (open) fetchNextOrderNumber();
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Order Number</DialogTitle>
              <DialogDescription>
                Change the order number for this job. Must be unique within your company.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-order-number">Order Number</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-order-number"
                    value={newOrderNumber}
                    onChange={(e) => setNewOrderNumber(e.target.value)}
                    placeholder={suggestedOrderNumber || "e.g., 25001"}
                    data-testid="edit-order-number-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (suggestedOrderNumber) {
                        setNewOrderNumber(suggestedOrderNumber);
                      } else {
                        const num = await fetchNextOrderNumber();
                        if (num) setNewOrderNumber(num);
                      }
                    }}
                    title="Use next available number"
                  >
                    Use Next
                  </Button>
                </div>
                {suggestedOrderNumber && (
                  <p className="text-xs text-slate-500">
                    Next available: <span className="font-mono font-semibold">{suggestedOrderNumber}</span>
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOrderNumberDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveOrderNumber}
                disabled={savingOrderNumber}
                data-testid="save-order-number-btn"
              >
                {savingOrderNumber ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Purchase Order Dialog */}
        <Dialog open={showPurchaseOrderDialog} onOpenChange={setShowPurchaseOrderDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-600" />
                Create Purchase Order / Quote Request
              </DialogTitle>
              <DialogDescription>
                Order #{order?.order_number} - Send to supplier for quote or order
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Load from Template */}
              {poTemplates.length > 0 && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg space-y-3">
                  <Label className="text-teal-800 font-medium flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Quick Load from Template
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {poTemplates.map(template => (
                      <Button
                        key={template.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => loadFromTemplate(template)}
                        className="border-teal-300 text-teal-700 hover:bg-teal-100"
                      >
                        {template.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Supplier Selection */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                <Label className="text-orange-800 font-medium">Supplier</Label>
                <select
                  value={poForm.supplier_id || 'custom'}
                  onChange={(e) => handleSupplierSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-orange-300 rounded-md focus:ring-orange-500 bg-white"
                >
                  <option value="custom">-- Enter email manually --</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                  ))}
                </select>
                
                {!poForm.supplier_id && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Supplier Name"
                      value={poForm.supplier_name}
                      onChange={(e) => setPoForm(prev => ({ ...prev, supplier_name: e.target.value }))}
                    />
                    <Input
                      type="email"
                      placeholder="Supplier Email *"
                      value={poForm.supplier_email}
                      onChange={(e) => setPoForm(prev => ({ ...prev, supplier_email: e.target.value }))}
                      required
                    />
                  </div>
                )}
              </div>

              {/* Stone Details */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                <Label className="text-slate-800 font-medium">Stone Details</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Stone Type</Label>
                    <Input
                      value={poForm.stone_type}
                      onChange={(e) => setPoForm(prev => ({ ...prev, stone_type: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Stone Code</Label>
                    <Input
                      value={poForm.stone_code}
                      onChange={(e) => setPoForm(prev => ({ ...prev, stone_code: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Colour</Label>
                    <Input
                      value={poForm.stone_colour}
                      onChange={(e) => setPoForm(prev => ({ ...prev, stone_colour: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Shape</Label>
                    <Input
                      placeholder="e.g. Ogee Top, Peon, etc."
                      value={poForm.shape}
                      onChange={(e) => setPoForm(prev => ({ ...prev, shape: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Dimensions */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <Label className="text-blue-800 font-medium">Dimensions (mm)</Label>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs text-blue-600">Width</Label>
                    <Input
                      type="number"
                      placeholder="mm"
                      value={poForm.dimensions.width}
                      onChange={(e) => setPoForm(prev => ({ 
                        ...prev, 
                        dimensions: { ...prev.dimensions, width: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600">Height</Label>
                    <Input
                      type="number"
                      placeholder="mm"
                      value={poForm.dimensions.height}
                      onChange={(e) => setPoForm(prev => ({ 
                        ...prev, 
                        dimensions: { ...prev.dimensions, height: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600">Depth</Label>
                    <Input
                      type="number"
                      placeholder="mm"
                      value={poForm.dimensions.depth}
                      onChange={(e) => setPoForm(prev => ({ 
                        ...prev, 
                        dimensions: { ...prev.dimensions, depth: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600">Thickness</Label>
                    <Input
                      type="number"
                      placeholder="mm"
                      value={poForm.dimensions.thickness}
                      onChange={(e) => setPoForm(prev => ({ 
                        ...prev, 
                        dimensions: { ...prev.dimensions, thickness: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>

              {/* Sundries */}
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-teal-800 font-medium">Additional Items</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPoForm(prev => ({
                      ...prev,
                      sundries: [...prev.sundries, { description: '', include: true }]
                    }))}
                    className="text-teal-600 border-teal-300"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </div>
                {poForm.sundries.length === 0 ? (
                  <p className="text-sm text-teal-600">No additional items</p>
                ) : (
                  <div className="space-y-2">
                    {poForm.sundries.map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="checkbox"
                          checked={item.include}
                          onChange={(e) => {
                            const updated = [...poForm.sundries];
                            updated[idx].include = e.target.checked;
                            setPoForm(prev => ({ ...prev, sundries: updated }));
                          }}
                          className="rounded"
                        />
                        <Input
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...poForm.sundries];
                            updated[idx].description = e.target.value;
                            setPoForm(prev => ({ ...prev, sundries: updated }));
                          }}
                          className="flex-1"
                          placeholder="Item description"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setPoForm(prev => ({
                              ...prev,
                              sundries: prev.sundries.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Special Instructions */}
              <div className="space-y-2">
                <Label>Special Instructions</Label>
                <textarea
                  value={poForm.special_instructions}
                  onChange={(e) => setPoForm(prev => ({ ...prev, special_instructions: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  placeholder="Any additional notes for the supplier..."
                />
              </div>

              {/* Previous POs */}
              {purchaseOrders.length > 0 && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <Label className="text-gray-700 font-medium">Previous Purchase Orders</Label>
                  <div className="mt-2 space-y-2">
                    {purchaseOrders.map(po => (
                      <div key={po.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="text-sm font-medium">{po.supplier_name || po.supplier_email}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                            po.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            po.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {po.status}
                          </span>
                        </div>
                        {po.status === 'sent' && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkStoneOrdered(po.id)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                          >
                            Mark Stone Ordered
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowPurchaseOrderDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSendPurchaseOrder}
                disabled={sendingPO || !poForm.supplier_email}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {sendingPO ? 'Sending...' : 'Send to Supplier'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Artwork Changes Dialog */}
        <Dialog open={manualChangesDialog} onOpenChange={setManualChangesDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Request Artwork Changes
              </DialogTitle>
              <DialogDescription>
                Enter the changes needed for the artwork. These notes will appear in the artwork section for reference.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="font-medium">Change Notes <span className="text-red-500">*</span></Label>
                <Textarea
                  value={manualChangeNotes}
                  onChange={(e) => setManualChangeNotes(e.target.value)}
                  placeholder="Describe what changes are needed for the artwork...&#10;&#10;e.g., Change font to Times New Roman, fix spelling of 'Elizabeth', adjust spacing between lines..."
                  rows={6}
                  className="resize-none"
                  data-testid="manual-change-notes"
                />
                <p className="text-xs text-slate-500">
                  These notes will be visible in the Artwork section so the team knows what changes to make.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setManualChangesDialog(false);
                  setManualChangeNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!manualChangeNotes.trim()) {
                    toast.error('Please enter the change notes');
                    return;
                  }
                  
                  setSubmittingManualChanges(true);
                  try {
                    // Update order status to changes-requested with notes
                    await axios.patch(`${API}/orders/${orderId}`, 
                      { 
                        status: 'changes-requested',
                        status_date: new Date(statusDate).toISOString(),
                        artwork_change_notes: manualChangeNotes.trim()
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    toast.success('Status updated to Changes Requested');
                    setManualChangesDialog(false);
                    setManualChangeNotes('');
                    setStatusDate(new Date().toISOString().slice(0, 16));
                    fetchOrderData();
                  } catch (error) {
                    console.error('Failed to update status:', error);
                    toast.error('Failed to update status');
                  } finally {
                    setSubmittingManualChanges(false);
                  }
                }}
                disabled={submittingManualChanges || !manualChangeNotes.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="submit-manual-changes"
              >
                {submittingManualChanges ? 'Updating...' : 'Set Changes Requested'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Alternative Email Dialog */}
        <Dialog open={altEmailDialog} onOpenChange={setAltEmailDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Send {altEmailType === 'all-artwork' ? 'All Artwork' : altEmailType?.charAt(0).toUpperCase() + altEmailType?.slice(1)}
              </DialogTitle>
              <DialogDescription>
                Send to the client's email or enter an alternative email address.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Client Email:</span> {order?.client_email || 'Not set'}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Alternative Email (optional)</Label>
                <Input
                  type="email"
                  value={altEmail}
                  onChange={(e) => setAltEmail(e.target.value)}
                  placeholder="Enter alternative email address..."
                  data-testid="alt-email-input"
                />
                <p className="text-xs text-slate-500">
                  Leave blank to send to the client's email above.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setAltEmailDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendWithAltEmail}
                disabled={sendingWithAltEmail || (altEmail && !altEmail.includes('@'))}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-with-alt-email"
              >
                {sendingWithAltEmail ? 'Sending...' : (altEmail ? `Send to ${altEmail}` : 'Send to Client')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contact Client Dialog */}
        <Dialog open={showContactClientDialog} onOpenChange={setShowContactClientDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal-600" />
                Contact Client
              </DialogTitle>
              <DialogDescription>
                Send an email to {order?.client_name || 'the client'} regarding order #{order?.order_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="contact-to-email">To Email</Label>
                <Input
                  id="contact-to-email"
                  type="email"
                  value={contactClientForm.to_email}
                  onChange={(e) => setContactClientForm({...contactClientForm, to_email: e.target.value})}
                  placeholder="client@email.com"
                  data-testid="contact-to-email"
                />
              </div>
              <div>
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  value={contactClientForm.subject}
                  onChange={(e) => setContactClientForm({...contactClientForm, subject: e.target.value})}
                  placeholder="Subject line"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <Label htmlFor="contact-body">Message</Label>
                <Textarea
                  id="contact-body"
                  value={contactClientForm.body}
                  onChange={(e) => setContactClientForm({...contactClientForm, body: e.target.value})}
                  placeholder="Type your message here..."
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="contact-body"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can use placeholders: {'{client_name}'}, {'{order_number}'}, {'{company_name}'}, {'{company_phone}'}, {'{company_email}'}
                </p>
              </div>

              {/* Attachments — multi-file picker, base64-encoded then POSTed
                  alongside the email. Capped at 10MB total in the handler. */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Attachments {contactAttachments.length > 0 && (
                    <span className="text-xs font-normal text-slate-500">
                      ({contactAttachments.length} file{contactAttachments.length === 1 ? '' : 's'}, {(contactAttachments.reduce((s,a)=>s+(a.sizeBytes||0),0)/1024/1024).toFixed(2)} MB)
                    </span>
                  )}</Label>
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleAddContactAttachment(Array.from(e.target.files || []));
                        e.target.value = '';  // allow re-selecting the same file
                      }}
                      data-testid="contact-attachment-input"
                    />
                    <span>📎 Add files</span>
                  </label>
                </div>
                {contactAttachments.length > 0 ? (
                  <ul className="text-sm border border-slate-200 rounded-md divide-y divide-slate-200 bg-white" data-testid="contact-attachments-list">
                    {contactAttachments.map((a, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-slate-800">{a.filename}</p>
                          <p className="text-xs text-slate-500">{(a.sizeBytes/1024).toFixed(1)} KB · {a.content_type}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveContactAttachment(i)}
                          className="text-red-600 hover:text-red-700 text-xs font-medium"
                          data-testid={`remove-attachment-${i}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-500">Optional — add PDFs, images or documents (10 MB total max).</p>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowContactClientDialog(false);
                  setContactClientForm({ to_email: '', subject: '', body: '' });
                  setContactAttachments([]);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSendContactEmail}
                disabled={sendingContactEmail || !contactClientForm.to_email || !contactClientForm.subject || !contactClientForm.body}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="send-contact-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingContactEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default OrderDetail;
